// src/automations.ts
var DRIVE_SHARE_MODES = [
  {
    value: "INHERIT",
    label: "Whoever the folder allows",
    description: "Adds no sharing of its own. The safe default."
  },
  {
    value: "DOMAIN",
    label: "Anyone in the workspace domain",
    description: "Readable by everyone on the configured Google Workspace domain."
  },
  {
    value: "ANYONE_WITH_LINK",
    label: "Anyone with the link",
    description: "Readable by anyone who has the URL, including outside the company."
  }
];
var SLACK_DELIVERY_FORMATS = [
  {
    value: "FULL",
    label: "The full report",
    description: "The whole document is posted, led by its Google Doc link when it was filed there."
  },
  {
    value: "HEADS_UP",
    label: "A heads-up with the Google Doc link",
    description: "A short message saying the report is ready, with its link. Needs Google Drive filing \u2014 if filing fails, the full report is posted instead."
  }
];
function slackFormatProblem(input) {
  if (input.slackFormat === "HEADS_UP" && !input.saveToDrive) {
    return 'A Slack heads-up links the Google Doc, so it needs "Save to Google Drive" turned on.';
  }
  return null;
}
var AUTOMATION_TRIGGERS = [
  {
    trigger: "DAILY_AT_TIME",
    label: "Every day at a time",
    description: "Runs once a day at the time you set, in the timezone you pick.",
    needsTime: true,
    sourceFilterLabel: null
  },
  {
    trigger: "WEEKLY_ON_DAYS",
    label: "Every week on chosen days",
    description: "Runs at the same time on the weekdays you tick \u2014 Mondays, or Tuesdays and Thursdays.",
    needsTime: true,
    needsDaysOfWeek: true,
    sourceFilterLabel: null
  },
  {
    trigger: "MONTHLY_ON_DATE",
    label: "Every month on a date",
    description: "Runs once a month on the date you pick. Months without that date run on their last day.",
    needsTime: true,
    needsDayOfMonth: true,
    sourceFilterLabel: null
  },
  {
    trigger: "ONCE_AT_DATETIME",
    label: "Once, at a date and time",
    description: "Runs a single time at the date and time you set, then never again.",
    needsTime: true,
    needsRunDate: true,
    sourceFilterLabel: null
  },
  {
    trigger: "GRANOLA_NOTE_RECEIVED",
    label: "New Granola meeting note",
    description: "Runs each time a meeting note is synced from Granola.",
    needsTime: false,
    sourceFilterLabel: "Only certain notes \u2014 comma-separated: folder=<Granola folder id>, title=<text> (note or calendar event title contains it), title_is=<text> (title is exactly it), person=<address or *pattern> (someone in the meeting \u2014 owner, attendee, organiser or invitee \u2014 is it, e.g. person=*@board.fun). Case-insensitive; several title rules fire on any one, likewise person rules, and a title and a person rule must both hold. A bare value is a folder id. Blank = every note."
  },
  {
    trigger: "SLACK_MESSAGE_RECEIVED",
    label: "New Slack message",
    description: "Runs on every message in a linked channel. Narrow it to one channel unless you mean every message in every linked channel.",
    needsTime: false,
    sourceFilterLabel: "Only messages from this Slack channel id (blank = any linked channel)"
  },
  {
    trigger: "KB_RUN_COMPLETED",
    label: "Ingestion run finished",
    description: "Runs each time a knowledge-base ingestion run settles in scope \u2014 a report on what a run just changed, or an alert when one fails. Runs started by an automation filing its own note never fire it, so two automations cannot feed each other forever.",
    needsTime: false,
    sourceFilterLabel: "Only certain runs \u2014 comma-separated tokens: source kinds (FILE_UPLOAD, GRANOLA, SLACK, EMAIL, DRIVE_FILE, LINKED_DOCUMENT, AUTOMATION_EDIT, MANUAL, MCP, USER_FEEDBACK), BATCH for batch-window runs, and outcomes COMPLETED / FAILED. Blank = every completed run; name FAILED to hear about failures."
  },
  {
    trigger: "CALENDAR_EVENT_UPCOMING",
    label: "Before a meeting starts",
    description: "Runs a set time before each meeting on a watched calendar \u2014 a brief that arrives while there is still time to read it. Fires once per occurrence, so a weekly meeting fires weekly; moving a meeting does not fire it again.",
    needsTime: false,
    sourceFilterLabel: "Lead time and what narrows it \u2014 comma-separated: lead=30 (minutes before, default 30), title=Board (case-insensitive substring of the meeting name), calendar=name@company.com. Blank = 30 minutes before every meeting on every watched calendar, which is almost never what you want."
  },
  {
    trigger: "CALENDAR_EVENT_ENDED",
    label: "After a meeting ends",
    description: "Runs a set time after each meeting on a watched calendar finishes \u2014 a post-meeting summary, once the notes have had a chance to arrive. Same filters as the before-it-starts trigger.",
    needsTime: false,
    sourceFilterLabel: "Delay and what narrows it \u2014 comma-separated: lead=15 (minutes after it ends, default 15), title=Board, calendar=name@company.com. Blank = 15 minutes after every meeting on every watched calendar."
  },
  {
    trigger: "EMAIL_RECEIVED",
    label: "New email",
    description: "Runs each time a message arrives in a watched mailbox. Fires on arrival rather than on the knowledge base having absorbed it, so it keeps working while ingestion is backed up.",
    needsTime: false,
    sourceFilterLabel: "Only mail from this mailbox \u2014 an address, or address:Label_7 for one label inside it (blank = any watched mailbox)"
  },
  {
    trigger: "DRIVE_FILE_RECEIVED",
    label: "New or edited Drive document",
    description: "Runs each time a document lands in \u2014 or is edited in \u2014 a watched Shared Drive. An edit is an arrival here, so a document revised three times in a morning fires three times.",
    needsTime: false,
    sourceFilterLabel: "Only documents from this Shared Drive or folder id (blank = any watched drive)"
  }
];
function automationTriggerLabel(trigger) {
  return AUTOMATION_TRIGGERS.find((t) => t.trigger === trigger)?.label ?? trigger;
}
function automationTriggerDefinition(trigger) {
  return AUTOMATION_TRIGGERS.find((t) => t.trigger === trigger) ?? AUTOMATION_TRIGGERS[0];
}
var TIME_OF_DAY_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;
function isValidTimeOfDay(value) {
  return TIME_OF_DAY_PATTERN.test(value);
}
var AUTOMATION_TEMPLATES = [
  {
    id: "pre-meeting-brief",
    name: "Pre-meeting brief",
    summary: "Open threads, owners and carried-over items ahead of a recurring meeting.",
    suggestedTrigger: "DAILY_AT_TIME",
    prompt: `Produce a pre-meeting brief for the Board Weekly meeting.

Gather, in this order:
1. current_datetime \u2014 you need today's date for the heading and to bound the windows below.
2. automation_previous_runs \u2014 the last brief and, if one exists, the last post-meeting summary
   (pass automationName to reach another automation's output). Anything left unresolved there is
   what goes under "Carried over from last time".
3. kb_outline, then kb_search / kb_read on the branches that cover this project's live work \u2014
   status, milestones, vendors, QA. Use kb_ask for anything that needs a synthesized answer
   rather than a page.
4. granola_list_notes and slack_list_messages for the last 7 days, to catch threads that moved
   since the last occurrence but have not reached the knowledge base yet.

Then emit exactly this structure, with real values in place of the brackets:

# [Meeting Name], [Date]

**Goal of today:** [one line: what this meeting needs to land on]

## Open threads

### [Topic]

- Status: [where this currently stands]
- Open questions: [what's still unresolved]
- Owner: [Name], [what they owe]
- Links: [decks, docs, KB paths]

## Carried over from last time

[Unresolved items from the previous occurrence]

Rules: one "### Topic" block per live thread, ordered by how much the meeting needs to decide on
it. Leave out any section or bullet you have no evidence for rather than emitting the bracketed
placeholder. If something important is unknown, say so in one line under the section it belongs
to. Never state a status you did not read from a tool.`
  },
  {
    id: "post-meeting-summary",
    name: "Post-meeting action items",
    summary: "Turns a meeting note into a summary with owned action items.",
    suggestedTrigger: "GRANOLA_NOTE_RECEIVED",
    prompt: `A meeting note has just landed. Turn it into a post-meeting summary.

Gather:
1. granola_read_note on the triggering note \u2014 this is your primary source and most of the output
   comes from it.
2. current_datetime for the heading.
3. kb_search / kb_read for anything the note refers to but does not explain \u2014 a project's prior
   status, who owns what, a decision it revisits. Enough context that the summary reads on its own.
4. automation_previous_runs if this meeting recurs, so you can tell what changed since last time
   rather than restating it.

Then emit exactly this structure:

# [Meeting Name], [Date], Post-Meeting Summary

## Meeting context

[One or two sentences: what this meeting was for and what it was focused on]

## Announcements

[Only if there was something time-sensitive that was not discussion \u2014 otherwise omit this section]

## Status & progress

[Pick the shape that fits what was actually discussed. Per-person for a standup:
- **[Name]**
  - Since last time: [what happened]
  - This period: [what's next]
  - Blockers: [anything in the way]

Per-project for a status sync:
- **Project status:** current status, progress since the last sync, completed milestones
- **Current roadblocks & risks:** anything threatening the timeline
- **Upcoming tasks and milestones:** what's next, with deadlines]

## Discussion

[Topic notes that belong to neither a person's update nor the status section: decisions made,
open debates, things to revisit. Omit if there were none.]

## Team collaboration & action items

- [ ] [Owner]: [task] ([deadline or context])
- [ ] Unassigned: [task]

Rules: every action item needs an owner or the literal word Unassigned \u2014 never invent one. Only
include what the note supports; omit empty sections rather than emitting placeholders. Keep names
exactly as they appear in the note.`
  },
  {
    id: "morning-digest",
    name: "Morning digest",
    summary: "What moved yesterday, posted to Slack before the day starts.",
    suggestedTrigger: "DAILY_AT_TIME",
    prompt: `Write a short morning digest of what moved in this project yesterday.

Gather: current_datetime, then granola_list_notes and slack_list_messages for the last 24 hours,
then kb_search for any knowledge base page those touched.

Emit at most 200 words of markdown:

**Yesterday** \u2014 3 to 5 bullets, each one thing that actually happened, with who and where.
**Today's watch list** \u2014 anything with a deadline or an unanswered question.

If nothing happened, say exactly that in one line. Do not pad. No preamble, no sign-off.`
  },
  {
    id: "ingestion-digest",
    name: "Ingestion & review digest",
    summary: "What the ingestion agent did in the last day, what failed, and what waits for review.",
    suggestedTrigger: "DAILY_AT_TIME",
    prompt: `Report on what the knowledge base's own machinery did, for someone who supervises it.

Gather, in this order:
1. current_datetime \u2014 for the heading and to bound the windows below.
2. automation_previous_runs \u2014 the last digest, so "still waiting" and "resolved since yesterday"
   mean something.
3. kb_list_runs for the last 1 day \u2014 every run, then a second call with status FAILED over the
   last 3 days so a failure is never mentioned only once and forgotten.
4. kb_read_run on any run that failed, wrote into several projects, or flagged more than two
   changes \u2014 the run's own reasoning is the material, not the counts.
5. kb_list_changes with status PENDING (no window \u2014 the whole queue is the point), and
   kb_read_change on the two or three that have waited longest or look riskiest (deletes, merges).

Then emit exactly this structure, with real values in place of the brackets:

# Ingestion digest, [Date]

**In one line:** [runs, pages changed, failures, review queue depth \u2014 or "a quiet day"]

## What was absorbed

[One bullet per run worth words: what arrived, where it was filed, what changed. Group the
trivial rest into a single line with counts.]

## Failures

[Each failed run: what it was handed, what the error says, and whether the content went back in
the queue. Quote the error.]

## Waiting for review

[The pending queue: how deep, how old the oldest is, and one bullet per change a reviewer should
look at first \u2014 why it was flagged, in the agent's own words.]

Rules: leave out any section you have no evidence for rather than emitting the bracketed
placeholder \u2014 a day with no failures has no Failures section. Never state a status you did not
read from a tool. If nothing ran and nothing waits, say exactly that in one line. Do not pad.`
  }
];
function automationTemplate(id) {
  return AUTOMATION_TEMPLATES.find((t) => t.id === id);
}
var AUTOMATION_TOOL_CATALOG = [
  {
    name: "current_datetime",
    group: "context",
    description: `Today's date and time in this automation's timezone, plus what triggered this run, when this automation last ran, and every named window already resolved to exact instants \u2014 backward (today, yesterday, this_week, last_week, this_month, last_month, since_last_run) and forward (rest_of_today, tomorrow, next_7_days, next_week). Call this first: every date in your output and every search window depends on it, and you have no other way to know when "now" is.`,
    args: "none",
    promptTip: 'Name it first in the gather list. The agent has no clock otherwise, so every "yesterday" and "this week" in a prompt resolves through it \u2014 and for an event trigger it also hands back the triggering item\'s id (triggerRunId for an ingestion run, triggerCalendarEventId for a meeting). Its resolved windows are what the listing tools take as `window`, which beats counting days back: they align to local midnight in this automation\'s timezone, and since_last_run is the one a recurring report actually wants. The four forward windows only match on calendar_list_events \u2014 every other source here is a record of something that already happened.'
  },
  {
    name: "kb_projects",
    group: "context",
    description: "Every project this run can read, with how many pages each holds, how many edits are waiting for review, when it was last written to and how many days ago that was, and its top-level branches. One call, whatever the size of the tree \u2014 use it instead of an outline per project when the prompt spans more than one, and when the question is which areas have gone quiet rather than what any one of them says.",
    args: "includeBranches (default true)",
    promptTip: 'The whole of a "what has gone quiet" or portfolio-health section: daysSinceWritten is already computed, so the prompt can say "flag anything not written to in 14 days" and mean it. Also the cheapest way to get real branch paths to name in a later gather step.'
  },
  {
    name: "kb_outline",
    group: "context",
    description: "The knowledge base's full structure as an indented tree: every page's path, title and one-line summary. Cheap, and the fastest way to see what this project knows before you search it. When more than one project is in scope the tree is grouped by level and pages are addressed as `Level:path` \u2014 pass that form back to kb_read, because a bare path can exist in several projects at once.",
    args: "none",
    promptTip: "Worth naming when the prompt covers a whole area rather than a page you can name. Skip it when the prompt already says which paths to read."
  },
  {
    name: "kb_search",
    group: "context",
    description: "Hybrid lexical + semantic search over the knowledge base. Best for topics and concepts. **Ask everything you need at once** by passing `queries` \u2014 one call with several questions costs a fraction of several calls, and the answers come back grouped under the question each one answers. Returns snippets, each addressed as `Level:path` when the scope spans projects, with the `section` of the page that matched best \u2014 open anything you intend to quote with kb_read, passing that address back verbatim, or `address#section` to read only that part of a long page. Each question also returns `notYetInKnowledgeBase`: meetings, messages, emails and documents matching every term that have arrived but not been written into any page yet \u2014 the pages are rewritten once a day, so these are newer than anything a page says. Open one with granola_read_note.",
    args: 'queries[] (1\u201310, ask them all at once) or query; pathPrefix (one branch, e.g. "engineering"); limit per question (1\u201320, default 8 for one / 5 for several)',
    promptTip: 'Say what to search for, not just "check the knowledge base" \u2014 name the topics, and the branch if you know it. Snippets only, so pair it with kb_read.'
  },
  {
    name: "kb_grep",
    group: "context",
    description: "Literal or regex match over knowledge base titles, summaries and bodies. Use this for things that must match exactly \u2014 a person's name, a ticket id, a version string, a milestone name \u2014 where semantic search drifts to merely related text.",
    args: "pattern; regex (default false); limit (1\u201350, default 20). Hits are addressed as `Level:path` when the scope spans projects.",
    promptTip: "Name it in a prompt that turns on exact strings \u2014 a person, a ticket, a release. Semantic search will happily return something merely related."
  },
  {
    name: "kb_read",
    group: "context",
    description: "Knowledge base pages in full, optionally with their child sections. **Read every page you need at once** \u2014 four pages in one call costs a fraction of four calls. Accepts bare paths or the `Level:path` form outline, search and grep return; pass those back verbatim, since a bare path that exists in several projects has to be guessed at and you will not be told which one you got. Append `#section` (the section a search hit named) to read one part of a long page; the result lists its other sections. Read before you quote \u2014 a search snippet is not enough to state a status from.",
    args: "paths[] (1\u20136, read them all at once) or path; includeChildren; includeCitations",
    promptTip: 'Back it with a rule \u2014 "never state a status you did not read from a tool" \u2014 which is what stops a snippet being reported as a fact.'
  },
  {
    name: "kb_ask",
    group: "context",
    description: 'Ask the knowledge base a question in plain language and get a synthesized, cited answer. This runs its own search-and-read loop, so use it when the answer spans several pages ("what is the current state of vendor onboarding?") rather than when you need one specific page.',
    args: "question",
    promptTip: "For questions that span pages. It spends a nested model call, so a prompt that already knows which page it wants should say kb_search/kb_read instead."
  },
  {
    name: "granola_list_notes",
    group: "sources",
    description: "Meeting notes synced from Granola in a time window, newest first: title, when it happened, attendees and an opening extract. This is raw meeting material \u2014 use it to catch what has happened but has not been written into the knowledge base yet. Read one in full with granola_read_note.",
    args: "window (today | yesterday | this_week | last_week | this_month | last_month | since_last_run) or days (1\u201390, default 7) or since/until; folderId; titleContains; limit (1\u201350, default 25). `truncated: true` means older notes in the window were left out.",
    promptTip: "Give an explicit window. This is where *recent* material lives: arrivals are usually batched into the knowledge base once a day, so today's meeting is here before it is on a page."
  },
  {
    name: "granola_read_note",
    group: "sources",
    description: "One captured item in full by its feedItemId \u2014 a Granola meeting note, a Slack message, an uploaded document. If this run was triggered by a note, current_datetime gives you its id.",
    args: "feedItemId",
    promptTip: 'For an event-triggered automation, say "read the triggering note in full" \u2014 that item is the reason the run exists and its opening is already inlined, but the rest is only here.'
  },
  {
    name: "slack_list_messages",
    group: "sources",
    description: "Messages captured from the project's linked Slack channels in a time window, newest first. Each message is short, so these come back with their text rather than an extract.",
    args: "window (today | yesterday | this_week | last_week | this_month | last_month | since_last_run) or days (1\u201390, default 7) or since/until; channelId; limit (1\u2013100, default 40). `truncated: true` means older messages in the window were left out.",
    promptTip: "Narrow it to a channel when the prompt is about one conversation; a wide window over every linked channel is mostly noise."
  },
  {
    name: "calendar_list_events",
    group: "sources",
    description: "Meetings on the organization's watched calendars in a time window: title, when, who is invited and whether each has an agenda. **This is the one source that looks forward** \u2014 every other tool here reports what already happened, so the forward windows (rest_of_today, tomorrow, next_7_days, next_week) only mean anything on this one. Read an agenda and the full attendee list with calendar_read_event. Read **live** from the calendars at the moment of the call, so a meeting moved or called off minutes ago already shows as such. A meeting several watched people attend comes back **once**, with `calendars` naming every calendar it is on and `responses` how each holder answered \u2014 a meeting someone declined is still on their calendar, so check `responses` before listing it as theirs. `truncated: true` means meetings were left out, and `unreadableCalendars` names calendars that could not be read this time \u2014 never present either as the whole day. A meeting marked `withheld` had its title and agenda left out by content screening: report it as withheld, never guess what it is. Cancelled meetings are excluded unless you ask for them, which matters when a brief has to say something was called off.",
    args: "window (the backward anchors, plus rest_of_today | tomorrow | next_7_days | next_week) or days or since/until; calendarIds[] (a person's address, to list only their meetings); titleContains; includeCancelled; limit (1\u2013200 meetings, default 60)",
    availability: 'offered wherever the sources hub is configured, whether or not any calendar is readable through it. With none watched and subscribed, both tools answer with empty lists rather than being withheld \u2014 so an empty answer can mean "no calendar here", not only "nothing scheduled". Check automation_options or context_guide for which calendars are actually readable before writing a prompt that depends on one.',
    promptTip: "The backbone of a pre-meeting or day-ahead brief: name a forward window, because the default backward ones return meetings that have already started. titleContains is how a brief about one recurring meeting finds its next occurrence. Pair it with calendar_read_event \u2014 the listing says a meeting has an agenda, not what the agenda says. A brief about one person passes their address as calendarIds: every watched calendar is listed otherwise, and a busy organization's day is far longer than one person's."
  },
  {
    name: "calendar_read_event",
    group: "sources",
    description: "One meeting in full by its eventId, read live: the agenda as written, every attendee with whether they accepted, the organizer, location, conference link and any attached documents. calendar_list_events tells you a meeting exists and whether it has an agenda; this is the only way to read it. `found: false` with a `note` means the calendar could not be read just now \u2014 say the meeting could not be checked, not that it does not exist.",
    args: "eventId (from calendar_list_events, or from current_datetime for a calendar-triggered run)",
    availability: "offered wherever calendar_list_events is \u2014 the two arrive and leave together.",
    promptTip: 'For a calendar-triggered automation, say "read the triggering event in full first" \u2014 its id arrives with the trigger, and the agenda is usually the only statement of what the meeting is for. Worth naming whenever a brief should reflect who actually accepted rather than who was invited.'
  },
  {
    name: "automation_previous_runs",
    group: "sources",
    description: `What this automation produced last time, or what another automation anywhere in this run's read scope produced. This is how you avoid repeating yourself and how you fill a "since last time" or "carried over" section: read the previous output and report what changed or what is still open. Note it returns the previous *document* \u2014 to track how long something has been open, use the ledger instead.`,
    args: "limit (1\u20135, default 1); automationName (loose match, to read another automation in scope)",
    promptTip: 'The mechanism behind "carried over from last time", and the only way a recurring document knows what it already said. Cross-read with automationName \u2014 a pre-meeting brief reading the last post-meeting summary needs nothing to have reached the knowledge base first.'
  },
  {
    name: "feed_list_items",
    group: "sources",
    description: 'Everything captured into this scope in a time window, whatever it came from \u2014 meeting notes, Slack, uploads, pastes, MCP writes, reviewer feedback \u2014 newest first, with an opening extract each. The per-source lists (granola_list_notes, slack_list_messages) are better when you know the source; this is for "what arrived at all". Read one in full with granola_read_note.',
    args: "window (today | yesterday | this_week | last_week | this_month | last_month | since_last_run) or days (1\u201390, default 7) or since/until; sourceType (FILE_UPLOAD | GRANOLA | SLACK | EMAIL | DRIVE_FILE | LINKED_DOCUMENT | AUTOMATION_EDIT | MANUAL | MCP | USER_FEEDBACK | USER_EDIT | CLI_IMPORT); limit (1\u201350, default 20)",
    promptTip: 'For prompts about arrivals as such \u2014 "what came in yesterday, from where" \u2014 rather than about one source. Pair it with kb_list_runs to say what became of each arrival.'
  },
  {
    name: "feed_search",
    group: "sources",
    description: "Search captured source material by meaning or by name \u2014 meeting notes, Slack, uploads \u2014 rather than listing the newest few. This is how you reach anything older than the last couple of days: the listing tools return the most recent N in a window, and at the rate meetings arrive that is about two days, so the previous occurrence of a weekly meeting is already past the end of them. **Ask every question you have in one call** by passing `queries`: the answers come back grouped under the question each one answers, and one call with ten questions costs a fraction of ten calls. Pass titleContains to find earlier occurrences of a recurring meeting by its name, queries to find material about topics, or both. Returns extracts \u2014 read one in full with granola_read_note.",
    args: "queries[] (1\u201310, ask them all at once) or query; titleContains; sourceType; window or days (default 30) or since/until; limit per question (1\u201330, default 10 for one / 5 for several)",
    promptTip: 'Name it whenever the prompt reaches back past a few days or turns on a recurring meeting by name \u2014 "the last three Board QA Weekly notes" is titleContains, not a longer days window. **Say to batch the questions**: a prompt that says "for each commitment, feed_search its subject" spends one whole turn per commitment, where "pass every subject as queries in one feed_search call" spends one. Results come back grouped under the question each answers, including the ones that matched nothing. Pair it with granola_read_note: this returns extracts.'
  },
  {
    name: "ledger_list",
    group: "sources",
    description: "What this automation is tracking between its runs, oldest first \u2014 each entry with how many days it has been open and when it was last seen. This is the memory automation_previous_runs cannot give you: the previous run's document says what was true last week, the ledger says how long something has been true. Defaults to open entries. Pass openLongerThanDays to get the ageing filter that turns a list of two hundred into a report of twenty.",
    args: "status (OPEN | RESOLVED | DISMISSED | ALL, default OPEN); openLongerThanDays; automationName (read another automation's ledger); limit (1\u2013100, default 50); offset",
    promptTip: 'Read it before gathering, not after: it tells you what you are already tracking, so the run reports movement rather than re-deriving the whole list. The openLongerThanDays filter is usually the report \u2014 "open at least 7 days and not seen since" is a whole prompt.'
  },
  {
    name: "ledger_record",
    group: "sources",
    description: `Record what this run found, so the next run knows it. Each entry needs a key you choose and can reproduce \u2014 the same key next week refreshes that entry instead of adding a second one, which is what lets the ledger count how long something has been open. The date an entry was first recorded is never moved by a later sighting. Recording does not change an entry's status: it says "I saw this again", not "this is open again".`,
    args: "entries[] (1\u201350), each: key; title; detail; owner; dueAt; data",
    promptTip: "Tell the prompt how to build the key, with an example \u2014 a key the agent cannot reproduce next week silently turns one item into a new one every run, which reads as everything always being new. Keys drawn from stable facts (owner + subject) survive rewording; keys drawn from the sentence do not."
  },
  {
    name: "ledger_resolve",
    group: "sources",
    description: "Close entries that have happened (RESOLVED) or that are not worth chasing (DISMISSED), or reopen one (OPEN). Takes the keys ledger_list gave you. Keys that matched nothing come back in notFound rather than failing.",
    args: "keys[] (1\u201350); status (RESOLVED | DISMISSED | OPEN); resolution",
    promptTip: 'Pair it with the gather step: "if a later meeting says an item was done, resolve it with a one-line resolution." Without this the ledger only ever grows, and the report stops being read.'
  },
  {
    name: "kb_page_history",
    group: "context",
    description: 'How one page got to what it says now: its revisions newest first, when each was written, whether a person wrote it by hand or the ingestion agent did, and optionally what changed between each and the one before. kb_read gives you the page as it stands; this is what answers "what changed here since last week" and "was this edited by hand".',
    args: "path; window or days or since; limit (1\u201320, default 5); includeDiff",
    promptTip: 'The backbone of any "what changed" section about a named page. Ask for includeDiff only on the few revisions you intend to quote \u2014 a diff of a long page is long.'
  },
  {
    name: "kb_list_runs",
    group: "record",
    description: "Ingestion runs that touched this scope, newest first: what each absorbed, where it wrote (a routed run lists every destination), what it created/updated/flagged, its status and any error. Paginate with limit/offset; narrow by status, sourceType, or a day window. Open one with kb_read_run.",
    args: "status (QUEUED | PROCESSING | COMPLETED | FAILED); sourceType; window (a named window, e.g. since_last_run) or days or since; limit (1\u201350, default 15); offset",
    promptTip: `The backbone of an ingestion digest: list the window's runs, then open the ones that matter. Failures are a status filter away \u2014 "list FAILED runs and explain each" is a complete monitoring prompt.`
  },
  {
    name: "kb_read_run",
    group: "record",
    description: "One ingestion run in full by its id: what it was handed, the placement reasoning and each destination's why, the pages it changed with each change's own reasoning and review state, counters, cost and any error. If this run was triggered by an ingestion run finishing, current_datetime hands you its id.",
    args: "runId",
    promptTip: `For a run-triggered automation, say "read the triggering run in full first" \u2014 its id arrives with the trigger, and this is where the run's reasoning and its changes live.`
  },
  {
    name: "kb_list_changes",
    group: "record",
    description: "The review queue: edits the ingestion agent applied, newest first \u2014 each with kind, page, confidence, review status and who resolved it. Defaults to PENDING (applied but awaiting a human); pass a status or ALL for history, or a runId for one run's changes. Open one with kb_read_change.",
    args: "status (PENDING | ACCEPTED | REJECTED | KEPT_BOTH | SUPERSEDED | NOT_REQUIRED | ALL, default PENDING); runId; path (that page and everything beneath it) + exactPath; window or days or since; limit (1\u201350, default 20); offset",
    promptTip: `The queue's depth and age is the health signal: "list pending changes, group by page, say which have waited longest and why each was flagged" is the whole of a review digest.`
  },
  {
    name: "kb_read_change",
    group: "record",
    description: "One review item in full by its id: the agent's reasoning, why it was flagged (the confidence floor it fell under, or the fix run that requires confirmation), a compact diff of what actually changed on the page, and the review verdict if one has been given.",
    args: "changeId",
    promptTip: "Open the changes worth words rather than describing the list. The diff is what lets a report quote what a page used to say against what it says now."
  },
  {
    name: "slack_post_message",
    group: "actions",
    description: "Post a message to one of this project's linked Slack channels, or to any of this automation's configured Slack destinations (a channel or a person). Only use this when your instruction asks you to post something \u2014 if this automation is configured to deliver its output to Slack, that happens on its own after you finish, and posting here as well would send it twice.",
    args: "text (ordinary Markdown, converted on the way out); channelId (defaults to the configured destination)",
    availability: "offered when Slack is configured and there is somewhere it may post: a channel pinned to the project, or a Slack destination set on the automation itself (a channel or a person) \u2014 either one is enough. Anything outside that list is refused \u2014 including any other person, so this cannot be used to message somebody the automation was not set up to message.",
    promptTip: "Do not ask for a post in the prompt if the automation is already configured to deliver to Slack \u2014 that sends the document twice. Ask for it only when a run should post something *other* than its document, e.g. a one-line heads-up."
  },
  {
    name: "kb_save_note",
    group: "actions",
    description: "File a document into the knowledge base so it becomes part of what this project knows. The knowledge base agent decides where it belongs and merges it with what is already there, so pass finished prose, not notes. Use this when your instruction asks for the result to be kept rather than only reported.",
    args: "title; content (Markdown)",
    availability: "offered when the worker is configured to file notes back into the knowledge base.",
    promptTip: "Only when the result should be *kept* rather than only reported. It hands the document to the ingestion agent, which places and merges it \u2014 so ask for finished prose, and expect it to be absorbed into existing pages rather than stored as its own."
  },
  {
    name: "finish",
    group: "terminal",
    description: "Deliver the finished document and end the run. Markdown, exactly as it should be read \u2014 this is what gets shown in the UI, filed in Google Drive when that is on, and posted to Slack (or, with a heads-up Slack format, summarized there in one line beside a link to the Doc). Call this once, and only when you have gathered what you need. When what triggered the run turns out not to be what the instruction is about, or there is nothing worth anyone's attention, finish with deliver: false and a skipReason: the run still completes and keeps its output in the console, but nothing is posted or filed.",
    args: "output (the document, Markdown); summary (one line for the run log); deliver (false = keep it in the console only; offered when Slack or Drive delivery is on); skipReason (why, with deliver: false); headsUp (only on automations whose Slack format is a heads-up: the one sentence Slack carries)",
    promptTip: 'The output skeleton in the prompt is what this emits. Say "emit exactly this structure" and give the shape \u2014 headings and all \u2014 rather than describing it.'
  }
];
function automationToolDefinition(name) {
  return AUTOMATION_TOOL_CATALOG.find((t) => t.name === name);
}
function automationToolDescription(name) {
  const found = automationToolDefinition(name);
  if (!found) throw new Error(`No automation tool catalog entry for "${name}"`);
  return found.description;
}

// src/citations.ts
function normalizeQuote(text) {
  return text.toLowerCase().replace(/[‘’‛′]/g, "'").replace(/[“”‟″]/g, '"').replace(/[–—−]/g, "-").replace(/ /g, " ").replace(/\s+/g, " ").trim();
}
function quoteAppearsIn(quote, haystack) {
  const plain = normalizeQuote(quote);
  if (plain.length === 0) return false;
  if (normalizeQuote(haystack).includes(plain)) return true;
  const needle = normalizeRenderedQuote(quote);
  if (needle.length === 0) return false;
  return normalizeRenderedQuote(haystack).includes(needle);
}
function stripInlineMarkdown(text) {
  const syntax = markdownSyntaxIndices(text);
  if (syntax.size === 0) return text;
  let out = "";
  for (let i = 0; i < text.length; i++) if (!syntax.has(i)) out += text[i];
  return out;
}
function markdownSyntaxIndices(text) {
  const syntax = /* @__PURE__ */ new Set();
  const patterns = [
    { re: /```[\s\S]*?```/g, content: null },
    { re: /`([^`]*)`/g, content: 1 },
    { re: /!\[([^\]]*)\]\([^)]*\)/g, content: 1 },
    { re: /\[([^\]]*)\]\([^)]*\)/g, content: 1 },
    { re: /(\*\*|__)([\s\S]*?)\1/g, content: 2 },
    { re: /(\*|_)([\s\S]*?)\1/g, content: 2 },
    { re: /^\s{0,3}#{1,6}\s+/gm, content: null },
    { re: /^\s*([-*+]|\d+\.)\s+/gm, content: null },
    // The flattened bullet. The leading whitespace is kept so the words either side stay separate
    // words — without it `buyer. - Path A` would collapse to `buyer.Path A` and match nothing, and
    // `$` covers a quote that happens to end on the marker, so the rule fires the same way at both
    // ends of a string.
    //
    // **Bullets only — `\d+.` is deliberately not recognised mid-line**, though the agent flattens
    // numbered lists exactly as it flattens bulleted ones. It was tried, and it is not safe: a
    // number followed by a period and a space is far more often content than syntax, and stripping
    // it *removes* text. Measured on run `cmtbt9jfq`, a body reading `…the credits layer ships in
    // late 2026. Prohibited market sizes: …` lost the year, while the citation quoting it ended at
    // `late 2026.` and kept it, because there was no trailing space there for the rule to match.
    // One good citation turned bad to rescue another. The cost of leaving it out is a citation lost
    // whenever a numbered list is quoted inline; the cost of putting it in is arbitrary years and
    // quantities deleted from both sides of every comparison, which is worse and much harder to see.
    { re: /(\s)[-*+](\s+|$)/g, content: 1 },
    { re: /^\s*>\s?/gm, content: null }
  ];
  for (const { re, content } of patterns) {
    re.lastIndex = 0;
    let match;
    while ((match = re.exec(text)) !== null) {
      if (match[0].length === 0) {
        re.lastIndex += 1;
        continue;
      }
      const start = match.index;
      const end = start + match[0].length;
      let keepFrom = -1;
      let keepTo = -1;
      if (content !== null && match[content] !== void 0) {
        const at = match[0].indexOf(match[content]);
        if (at >= 0) {
          keepFrom = start + at;
          keepTo = keepFrom + match[content].length;
        }
      }
      for (let i = start; i < end; i++) {
        if (i >= keepFrom && i < keepTo) continue;
        syntax.add(i);
      }
    }
  }
  return syntax;
}
function normalizeRenderedQuote(text) {
  return normalizeQuote(stripInlineMarkdown(text));
}

// src/model-retry.ts
function modelRetryConfig(setting) {
  if (setting?.trim().toLowerCase() === "off") return void 0;
  return {
    strategy: "backoff",
    backoff: {
      initialInterval: 1e3,
      maxInterval: 2e4,
      exponent: 2,
      maxElapsedTime: 6e5
    },
    retryConnectionErrors: true
  };
}

// src/features.ts
var UI_CAPABILITIES = [
  {
    capability: "KB_REVIEW_CONFIDENCE",
    feature: "KB_REVIEW",
    label: "Confidence scores",
    description: "Shows how confident the agent was, on every change card and in a run's list of changes. Switch off to leave reviewers judging the diff and its evidence alone."
  },
  {
    capability: "KB_REVIEW_NODE_PATH",
    feature: "KB_REVIEW",
    label: "Node paths",
    description: "Shows where in the project each changed page sits (`engineering/auth/sessions`), under its title on every change card and in a run's list of changes. Switch off for a reader who wants the page name alone \u2014 but leave it on for anyone reviewing across branches, since titles repeat and the path is the only thing on the card that says which page this is."
  },
  {
    capability: "KB_CREATE_PAGE",
    feature: "KB",
    label: "Create pages by hand",
    description: 'Offers the "New page" button at the top of a project\u2019s knowledge base and "New page inside" in a page\u2019s row menu there. Switch off for someone who should read and review what the agent writes without authoring pages themselves \u2014 every other way into the knowledge base, including dropping documents in, is unaffected.'
  },
  {
    capability: "KB_QUALITY_SWEEP",
    feature: "KB_QUALITY",
    label: "Run sweeps and send fixes",
    description: 'Offers "Sweep now", the daily schedule, and the message box on a finding card in KB Sweeps \u2014 the three controls that set an agent running or writing. Switch off for someone who should read what the sweep found and dismiss or resolve cards without spending model time; a fix run\u2019s changes are judged like any run\u2019s, so one that conflicts, holds personal information or was written unsure still comes to review.'
  }
];
var UI_FEATURE_GROUPS = [
  // First, and headingless in the sidebar only — see UiFeatureGroup.
  { group: "STANDALONE", key: "GROUP_STANDALONE", label: "Workspace", heading: false },
  { group: "SETTINGS", key: "GROUP_SETTINGS", label: "Settings" }
];
var UI_FEATURES = [
  // One headingless group holds everything that is a view onto memory. There is no longer a
  // separate "knowledge base" section, because the knowledge base *is* the system — a heading
  // named after it said nothing. Order is reading order: ask it, review what changed in it,
  // automate over it, then see the raw material arriving.
  //
  // All four are org-level: they read *across* the hierarchy and take the project as a filter, so a
  // `/p/<slug>/` prefix would be a lie about their subject. Asking the knowledge base, reviewing what
  // the agent changed and watching content arrive are questions about the organization that you then
  // narrow — the opposite of how they were addressed before, when the URL fixed the project and
  // seeing two of them meant visiting two pages.
  { feature: "KB_ASK", group: "STANDALONE", label: "Ask KB", path: "kb-search", orgLevel: true },
  { feature: "KB_REVIEW", group: "STANDALONE", label: "KB Review", path: "kb-review", orgLevel: true },
  // The sweep agent's runs over the store and the cards it files. Org-level like the review queue,
  // because a misplaced page is by definition about two projects and a duplicate as often is.
  { feature: "KB_QUALITY", group: "STANDALONE", label: "KB Sweeps", path: "kb-sweeps", orgLevel: true },
  // The runs half of what used to be KB Review's second tab: every ingestion the agents have run,
  // org-wide. Its own key because a flag can only take away a whole entry, and "who may watch the
  // agents work" is a different question from "who reviews what they wrote".
  { feature: "ACTIVITY", group: "STANDALONE", label: "Activity", path: "activity", orgLevel: true },
  { feature: "AUTOMATIONS", group: "STANDALONE", label: "Automations", path: "automations", orgLevel: true },
  { feature: "FEED", group: "STANDALONE", label: "Feed", path: "feed", orgLevel: true },
  // Conversations with the Slack bot — your own, or (system admins) everyone's. Org-level because a
  // conversation reads across whichever projects the asker belongs to.
  { feature: "CHATS", group: "STANDALONE", label: "Slack Chats", path: "chats", orgLevel: true },
  // Last of the visible items, so it is a landing page only for someone with every view onto memory
  // switched off. Org-level because a space belongs to a person, not to a project.
  { feature: "SPACES", group: "STANDALONE", label: "Spaces", path: "spaces", orgLevel: true },
  // The one section that stays project-scoped, because a node is addressed *within* one project's
  // tree. `nav: false` for the same reason: you reach it by picking a project in the sidebar
  // hierarchy, not from a list of sections. It stays in this array regardless, because the array is
  // also the registry flags resolve against.
  { feature: "KB", group: "STANDALONE", label: "Knowledge Base", path: "kb", nav: false },
  // The only sections still scoped to one project, and both `nav: false` for the reason KB is:
  // they are reached by picking a project — from the tree row menu's Settings, where they are now
  // tabs — rather than from a list of sections. As flat entries here they sat among org-level items
  // and silently meant "the project you happen to be in", which is the whole complaint that moved
  // them. The `path` stays accurate because it is what a route guard redirects against.
  { feature: "INTEGRATIONS", group: "SETTINGS", label: "Integrations", path: "settings/integrations", nav: false },
  { feature: "ACCESS", group: "SETTINGS", label: "Access", path: "settings/access", nav: false },
  // The org-level counterpart of INTEGRATIONS, and the only thing left in this group's sidebar
  // list. Two entries rather than one page with a toggle: they are answerable to different people —
  // a project's own sources are its members' business, the organization's shared inbox is not — and
  // a flag can only take away a whole entry.
  { feature: "GLOBAL_INTEGRATIONS", group: "SETTINGS", label: "Sources", path: "sources", orgLevel: true },
  // Every project's rules on one page, with the router pinned first — org-level because rules are set
  // across the hierarchy in one place rather than one project at a time.
  { feature: "INGEST_RULES", group: "SETTINGS", label: "Ingest rules", path: "ingest-rules", orgLevel: true }
];
var ALL_UI_FEATURES = UI_FEATURES.map((f) => f.feature);
var ALL_UI_CAPABILITIES = UI_CAPABILITIES.map((c) => c.capability);
var ALL_UI_FEATURE_FLAG_KEYS = [
  ...ALL_UI_FEATURES,
  ...UI_FEATURE_GROUPS.map((g) => g.key),
  ...ALL_UI_CAPABILITIES
];
function allFeaturesEnabled() {
  return Object.fromEntries(ALL_UI_FEATURES.map((f) => [f, true]));
}
function resolveFeatureFlags(rows) {
  const flags = allFeaturesEnabled();
  const featureSet = new Set(ALL_UI_FEATURES);
  for (const row of rows) {
    if (featureSet.has(row.feature)) flags[row.feature] = row.enabled;
  }
  for (const group of UI_FEATURE_GROUPS) {
    const row = rows.find((r) => r.feature === group.key);
    if (row && !row.enabled) {
      for (const definition of UI_FEATURES) {
        if (definition.group === group.group) flags[definition.feature] = false;
      }
    }
  }
  return flags;
}
function allCapabilitiesEnabled() {
  return Object.fromEntries(ALL_UI_CAPABILITIES.map((c) => [c, true]));
}
function resolveCapabilityFlags(rows, features) {
  const flags = allCapabilitiesEnabled();
  const capabilitySet = new Set(ALL_UI_CAPABILITIES);
  for (const row of rows) {
    if (capabilitySet.has(row.feature)) flags[row.feature] = row.enabled;
  }
  for (const definition of UI_CAPABILITIES) {
    if (!features[definition.feature]) flags[definition.capability] = false;
  }
  return flags;
}
function capabilitiesOf(feature) {
  return UI_CAPABILITIES.filter((c) => c.feature === feature);
}
var NAV_UI_FEATURES = UI_FEATURES.filter((f) => f.nav !== false);
function firstEnabledFeature(flags) {
  return NAV_UI_FEATURES.find((definition) => flags[definition.feature]) ?? null;
}

// src/integrations.ts
var INTEGRATION_PROVIDERS = [
  { provider: "SLACK", label: "Slack", sourceNoun: "channel", sourceNounPlural: "channels", viaSourcesHub: false },
  { provider: "GRANOLA", label: "Granola", sourceNoun: "source", sourceNounPlural: "sources", viaSourcesHub: true },
  { provider: "GOOGLE_MAIL", label: "Gmail", sourceNoun: "mailbox", sourceNounPlural: "mailboxes", viaSourcesHub: true },
  { provider: "GOOGLE_DRIVE", label: "Google Drive", sourceNoun: "drive", sourceNounPlural: "drives", viaSourcesHub: true },
  {
    provider: "GOOGLE_CALENDAR",
    label: "Google Calendar",
    sourceNoun: "calendar",
    sourceNounPlural: "calendars",
    viaSourcesHub: true
  }
];
var HUB_PROVIDERS = INTEGRATION_PROVIDERS.filter((p) => p.viaSourcesHub).map(
  (p) => p.provider
);
function granolaMeetingKey(title, startsAt) {
  const at = (startsAt instanceof Date ? startsAt : new Date(startsAt)).toISOString();
  return `${at}|${title.trim().toLowerCase().replace(/\s+/g, " ")}`;
}
var INTEGRATION_SCOPES = [
  {
    scope: "PROJECT",
    label: "Pinned to this project",
    summary: "Everything from these sources becomes this project\u2019s knowledge.",
    detail: "The destination is decided here, once. Every message or note from a source below is filed into this project\u2019s knowledge base whatever it turns out to be about \u2014 nothing is routed, and nothing lands anywhere else."
  },
  {
    scope: "GLOBAL",
    label: "Routed by relevance",
    summary: "Each item is read, then filed in whichever projects it belongs to.",
    detail: "No destination is chosen up front. Content arrives at the organization, and the ingestion run that absorbs it decides where it belongs first \u2014 reading each project\u2019s purpose, the pages it already holds, and whatever in the organization resembles the material \u2014 then files each part in every project it genuinely concerns, which can be more than one. Anything that matches nothing is parked at the organization level rather than dropped."
  }
];

// src/paragraph-diff.ts
var STANDALONE_LINE = /^\s{0,3}(#{1,6}\s|[-*+]\s|\d{1,3}[.)]\s|>\s?)/;
var RULE = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/;
function splitParagraphs(text) {
  const out = [];
  for (const block of text.replace(/\r\n?/g, "\n").split(/\n\s*\n/)) {
    const lines = block.split("\n").filter((line) => line.trim() !== "" && !RULE.test(line));
    if (lines.length === 0) continue;
    let prose = [];
    const flush = () => {
      if (prose.length > 0) out.push(prose.join("\n").trim());
      prose = [];
    };
    for (const line of lines) {
      if (STANDALONE_LINE.test(line)) {
        flush();
        out.push(line.trim());
      } else if (out.length > 0 && prose.length === 0 && /^\s{2,}\S/.test(line) && STANDALONE_LINE.test(out[out.length - 1])) {
        out[out.length - 1] = `${out[out.length - 1]}
${line.trim()}`;
      } else {
        prose.push(line);
      }
    }
    flush();
  }
  return out.filter((paragraph) => paragraphKey(paragraph) !== "");
}
function paragraphKey(paragraph) {
  const unblocked = paragraph.split("\n").map((line) => line.replace(/^\s{0,3}(#{1,6}\s+|[-*+]\s+|\d{1,3}[.)]\s+|>\s?)/, "")).join(" ");
  const unescaped = unblocked.replace(/\\([\\`*_{}\[\]()#+\-.!|<>~])/g, "$1");
  return normalizeRenderedQuote(unescaped).replace(/\s*\|\s*/g, " | ").trim();
}
function measureEdit(before, after) {
  const a = splitParagraphs(before);
  const b = splitParagraphs(after);
  const ka = a.map(paragraphKey);
  const kb = b.map(paragraphKey);
  const lcs = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i2 = a.length - 1; i2 >= 0; i2--) {
    for (let j2 = b.length - 1; j2 >= 0; j2--) {
      lcs[i2][j2] = ka[i2] === kb[j2] ? lcs[i2 + 1][j2 + 1] + 1 : Math.max(lcs[i2 + 1][j2], lcs[i2][j2 + 1]);
    }
  }
  const changed = [];
  const added = [];
  const removed = [];
  let keptChars = 0;
  let pendingRemoved = [];
  let pendingAdded = [];
  const settle = () => {
    let from = 0;
    const unpairedAdded = [];
    for (const after2 of pendingAdded) {
      const match = pendingRemoved.findIndex((before2, k) => k >= from && similarity(before2, after2) >= REWRITE_SIMILARITY);
      if (match === -1) {
        unpairedAdded.push(after2);
        continue;
      }
      removed.push(...pendingRemoved.slice(from, match));
      const beforeKey = paragraphKey(pendingRemoved[match]);
      const kept = keptWordChars(beforeKey, paragraphKey(after2));
      changed.push({ before: pendingRemoved[match], after: after2, extended: kept === wordChars(beforeKey) });
      keptChars += kept;
      from = match + 1;
    }
    removed.push(...pendingRemoved.slice(from));
    added.push(...unpairedAdded);
    pendingRemoved = [];
    pendingAdded = [];
  };
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && ka[i] === kb[j]) {
      settle();
      keptChars += wordChars(ka[i]);
      i++;
      j++;
    } else if (j < b.length && (i >= a.length || lcs[i][j + 1] >= lcs[i + 1][j])) {
      pendingAdded.push(b[j]);
      j++;
    } else {
      pendingRemoved.push(a[i]);
      i++;
    }
  }
  settle();
  const total = Math.max(
    ka.reduce((sum, key) => sum + wordChars(key), 0),
    kb.reduce((sum, key) => sum + wordChars(key), 0)
  );
  const changedShare = total === 0 ? 0 : Math.min(1, Math.max(0, 1 - keptChars / total));
  return { changed, added, removed, changedShare: Math.round(changedShare * 1e3) / 1e3 };
}
var wordsOf = (key) => key.match(/[\p{L}\p{N}\p{M}$%]+/gu) ?? [];
var wordChars = (key) => wordsOf(key).reduce((sum, word) => sum + word.length, 0);
function keptWordChars(beforeKey, afterKey) {
  const a = wordsOf(beforeKey);
  const b = wordsOf(afterKey);
  let next = new Array(b.length + 1).fill(0);
  for (let i = a.length - 1; i >= 0; i--) {
    const row = new Array(b.length + 1).fill(0);
    for (let j = b.length - 1; j >= 0; j--) {
      row[j] = a[i] === b[j] ? next[j + 1] + a[i].length : Math.max(next[j], row[j + 1]);
    }
    next = row;
  }
  return next[0];
}
var REWRITE_SIMILARITY = 0.5;
function similarity(a, b) {
  const words = (text) => new Set(paragraphKey(text).split(/[^\p{L}\p{N}$%]+/u).filter((w) => w.length > 1));
  const wa = words(a);
  const wb = words(b);
  if (wa.size === 0 || wb.size === 0) return 0;
  let shared = 0;
  for (const word of wa) if (wb.has(word)) shared++;
  return 2 * shared / (wa.size + wb.size);
}
function hasSubstantiveChange(measurement) {
  return measurement.changed.length > 0 || measurement.added.length > 0;
}

// src/feedback-metrics.ts
var FEEDBACK_CHANNELS = ["slack-chat", "mcp", "brief-edit", "console-feedback", "slack-channel"];
var CHANGE_CLASSES = ["correction", "update", "new", "restructure"];
var ARRIVAL_OUTCOMES = [...CHANGE_CLASSES, "no-effect", "failed", "waiting"];
var SETTLED = ["correction", "update", "new", "restructure", "no-effect", "failed"];
function classifyChange(change) {
  if (change.flagReason === "CONFLICT") return { class: "correction", flagged: true };
  if (change.kind === "CREATE") return { class: "new", flagged: false };
  if (change.kind !== "UPDATE") return { class: "restructure", flagged: false };
  if (change.beforeBody === null || change.afterBody === null) return { class: "update", flagged: false };
  const m = measureEdit(change.beforeBody, change.afterBody);
  const rewritten = m.changed.some((c) => !c.extended);
  if (rewritten || m.removed.length > 0 && m.added.length > 0) return { class: "correction", flagged: false };
  if (m.added.length > 0 || m.changed.length > 0) return { class: "update", flagged: false };
  return { class: "restructure", flagged: false };
}
var RANK = { correction: 4, update: 3, new: 2, restructure: 1 };
function classifyArrival(arrival) {
  if (arrival.changes.length === 0) {
    const outcome = arrival.segmentStatus === "INGESTED" ? "no-effect" : arrival.segmentStatus === "FAILED" ? "failed" : "waiting";
    return { outcome, flaggedCorrection: false };
  }
  let best = "restructure";
  let flagged = false;
  for (const change of arrival.changes) {
    const c = classifyChange(change);
    if (RANK[c.class] > RANK[best]) best = c.class;
    flagged ||= c.flagged;
  }
  return { outcome: best, flaggedCorrection: flagged };
}
function emptyOutcomes() {
  return Object.fromEntries(ARRIVAL_OUTCOMES.map((o) => [o, { count: 0, chars: 0 }]));
}
function portionOf(items) {
  const outcomes = emptyOutcomes();
  let chars = 0;
  let flaggedCorrections = 0;
  for (const { arrival, outcome, flagged } of items) {
    outcomes[outcome].count++;
    outcomes[outcome].chars += arrival.chars;
    chars += arrival.chars;
    if (flagged) flaggedCorrections++;
  }
  const settledCount = SETTLED.reduce((n, o) => n + outcomes[o].count, 0);
  const settledChars = SETTLED.reduce((n, o) => n + outcomes[o].chars, 0);
  const round = (n) => Math.round(n * 1e3) / 1e3;
  const shares = (pick, total) => total === 0 ? null : Object.fromEntries(ARRIVAL_OUTCOMES.map((o) => [o, o === "waiting" ? 0 : round(pick(o) / total)]));
  return {
    arrivals: items.length,
    chars,
    outcomes,
    share: shares((o) => outcomes[o].count, settledCount),
    volumeShare: shares((o) => outcomes[o].chars, settledChars),
    flaggedCorrections
  };
}
function byChannel(items) {
  const channels = {};
  for (const channel of FEEDBACK_CHANNELS) {
    const subset = items.filter((i) => i.arrival.channel === channel);
    if (subset.length > 0) channels[channel] = portionOf(subset);
  }
  return channels;
}
function localDate(at, timeZone) {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(at);
}
function periodOf(at, groupBy, timeZone) {
  const day = localDate(at, timeZone);
  if (groupBy === "day") return day;
  const d = /* @__PURE__ */ new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - (d.getUTCDay() + 6) % 7);
  return d.toISOString().slice(0, 10);
}
function summarizeFeedback(arrivals, options) {
  const groupBy = options.groupBy ?? "day";
  const timeZone = options.timeZone ?? "UTC";
  const items = arrivals.map((arrival) => {
    const { outcome, flaggedCorrection } = classifyArrival(arrival);
    return { arrival, outcome, flagged: flaggedCorrection };
  });
  const people = /* @__PURE__ */ new Map();
  for (const item of items) {
    const person = item.arrival.person;
    if (!person) continue;
    const entry = people.get(person.key) ?? { person, items: [] };
    entry.items.push(item);
    people.set(person.key, entry);
  }
  const periods = /* @__PURE__ */ new Map();
  for (const item of items) {
    const key = periodOf(item.arrival.at, groupBy, timeZone);
    const list = periods.get(key) ?? [];
    list.push(item);
    periods.set(key, list);
  }
  return {
    from: options.from.toISOString(),
    until: options.until.toISOString(),
    groupBy,
    timeZone,
    totals: portionOf(items),
    channels: byChannel(items),
    people: [...people.values()].map(({ person, items: own }) => ({ ...person, total: portionOf(own), channels: byChannel(own) })).sort((a, b) => b.total.arrivals - a.total.arrivals),
    periods: [...periods.entries()].sort(([a], [b]) => a < b ? -1 : 1).map(([from, group]) => ({ from, total: portionOf(group), channels: byChannel(group) }))
  };
}

// src/schedule.ts
function zoneOffsetMs(at, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  }).formatToParts(at);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? "0");
  const hour = get("hour") % 24;
  const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), hour, get("minute"), get("second"));
  return asUtc - at.getTime();
}
function localDateIn(at, timeZone) {
  return new Intl.DateTimeFormat("en-CA", { timeZone }).format(at);
}
function zonedInstant(dateIso, timeOfDay, timeZone) {
  const guess = /* @__PURE__ */ new Date(`${dateIso}T${timeOfDay}:00Z`);
  return new Date(guess.getTime() - zoneOffsetMs(guess, timeZone));
}
function safeTimeZone(timeZone) {
  if (!timeZone) return "UTC";
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return timeZone;
  } catch {
    return "UTC";
  }
}
var DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
function isValidDateIso(value) {
  if (!value || !DATE_PATTERN.test(value)) return false;
  const parsed = /* @__PURE__ */ new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}
function addDays(dateIso, days) {
  const d = /* @__PURE__ */ new Date(`${dateIso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
function weekdayOf(dateIso) {
  return (/* @__PURE__ */ new Date(`${dateIso}T00:00:00Z`)).getUTCDay();
}
function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
function yearMonthOf(dateIso) {
  return [Number(dateIso.slice(0, 4)), Number(dateIso.slice(5, 7))];
}
function shiftMonth(year, month, by) {
  const m = month + by;
  if (m === 0) return [year - 1, 12];
  if (m === 13) return [year + 1, 1];
  return [year, m];
}
function monthlyDate(year, month, dayOfMonth) {
  const day = Math.min(dayOfMonth, daysInMonth(year, month));
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}
var SCHEDULE_TRIGGERS = [
  "DAILY_AT_TIME",
  "WEEKLY_ON_DAYS",
  "MONTHLY_ON_DATE",
  "ONCE_AT_DATETIME"
];
function isScheduleTrigger(trigger) {
  return SCHEDULE_TRIGGERS.includes(trigger);
}
var WEEKDAYS = [
  { value: 1, label: "Monday", short: "Mon" },
  { value: 2, label: "Tuesday", short: "Tue" },
  { value: 3, label: "Wednesday", short: "Wed" },
  { value: 4, label: "Thursday", short: "Thu" },
  { value: 5, label: "Friday", short: "Fri" },
  { value: 6, label: "Saturday", short: "Sat" },
  { value: 0, label: "Sunday", short: "Sun" }
];
function normalizeDaysOfWeek(days) {
  if (!days) return [];
  return [...new Set(days.filter((d) => Number.isInteger(d) && d >= 0 && d <= 6))].sort((a, b) => a - b);
}
function isValidDayOfMonth(day) {
  return typeof day === "number" && Number.isInteger(day) && day >= 1 && day <= 31;
}
function validateSchedule(schedule) {
  if (!isScheduleTrigger(schedule.trigger)) return null;
  if (!schedule.timeOfDay) return "A time of day is required for a scheduled automation";
  if (!isValidTimeOfDay(schedule.timeOfDay)) return "timeOfDay must be 24-hour HH:mm";
  switch (schedule.trigger) {
    case "WEEKLY_ON_DAYS":
      if (normalizeDaysOfWeek(schedule.daysOfWeek).length === 0) {
        return "Pick at least one day of the week";
      }
      return null;
    case "MONTHLY_ON_DATE":
      if (!isValidDayOfMonth(schedule.dayOfMonth)) {
        return "Pick a day of the month between 1 and 31";
      }
      return null;
    case "ONCE_AT_DATETIME":
      if (!isValidDateIso(schedule.runDate)) return "Pick the date to run on (YYYY-MM-DD)";
      return null;
    default:
      return null;
  }
}
function previousOccurrence(schedule, now = /* @__PURE__ */ new Date()) {
  if (validateSchedule(schedule) !== null) return null;
  const zone = safeTimeZone(schedule.timezone);
  const time = schedule.timeOfDay;
  const today = localDateIn(now, zone);
  switch (schedule.trigger) {
    case "DAILY_AT_TIME": {
      const todayAt = zonedInstant(today, time, zone);
      return todayAt <= now ? todayAt : zonedInstant(addDays(today, -1), time, zone);
    }
    case "WEEKLY_ON_DAYS": {
      const days = normalizeDaysOfWeek(schedule.daysOfWeek);
      for (let back = 0; back <= 7; back += 1) {
        const date = addDays(today, -back);
        if (!days.includes(weekdayOf(date))) continue;
        const at = zonedInstant(date, time, zone);
        if (at <= now) return at;
      }
      return null;
    }
    case "MONTHLY_ON_DATE": {
      let [year, month] = yearMonthOf(today);
      for (let back = 0; back <= 1; back += 1) {
        const at = zonedInstant(monthlyDate(year, month, schedule.dayOfMonth), time, zone);
        if (at <= now) return at;
        [year, month] = shiftMonth(year, month, -1);
      }
      return null;
    }
    case "ONCE_AT_DATETIME": {
      const at = zonedInstant(schedule.runDate, time, zone);
      return at <= now ? at : null;
    }
    default:
      return null;
  }
}
function nextOccurrence(schedule, now = /* @__PURE__ */ new Date()) {
  if (validateSchedule(schedule) !== null) return null;
  const zone = safeTimeZone(schedule.timezone);
  const time = schedule.timeOfDay;
  const today = localDateIn(now, zone);
  switch (schedule.trigger) {
    case "DAILY_AT_TIME": {
      const todayAt = zonedInstant(today, time, zone);
      return todayAt > now ? todayAt : zonedInstant(addDays(today, 1), time, zone);
    }
    case "WEEKLY_ON_DAYS": {
      const days = normalizeDaysOfWeek(schedule.daysOfWeek);
      for (let ahead = 0; ahead <= 7; ahead += 1) {
        const date = addDays(today, ahead);
        if (!days.includes(weekdayOf(date))) continue;
        const at = zonedInstant(date, time, zone);
        if (at > now) return at;
      }
      return null;
    }
    case "MONTHLY_ON_DATE": {
      let [year, month] = yearMonthOf(today);
      for (let ahead = 0; ahead <= 1; ahead += 1) {
        const at = zonedInstant(monthlyDate(year, month, schedule.dayOfMonth), time, zone);
        if (at > now) return at;
        [year, month] = shiftMonth(year, month, 1);
      }
      return null;
    }
    case "ONCE_AT_DATETIME": {
      const at = zonedInstant(schedule.runDate, time, zone);
      return at > now ? at : null;
    }
    default:
      return null;
  }
}
function ordinal(n) {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}
function joinWords(parts) {
  if (parts.length <= 1) return parts.join("");
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}
function describeSchedule(schedule) {
  const time = schedule.timeOfDay ?? "\u2014";
  const zone = schedule.timezone || "UTC";
  switch (schedule.trigger) {
    case "DAILY_AT_TIME":
      return `Every day at ${time} ${zone}`;
    case "WEEKLY_ON_DAYS": {
      const days = normalizeDaysOfWeek(schedule.daysOfWeek);
      if (days.length === 0) return `Weekly \u2014 no days picked yet`;
      if (days.length === 7) return `Every day at ${time} ${zone}`;
      const labels = WEEKDAYS.filter((d) => days.includes(d.value)).map((d) => d.label);
      return `Every ${joinWords(labels)} at ${time} ${zone}`;
    }
    case "MONTHLY_ON_DATE": {
      if (!isValidDayOfMonth(schedule.dayOfMonth)) return "Monthly \u2014 no date picked yet";
      const suffix = schedule.dayOfMonth > 28 ? " (or the last day, in shorter months)" : "";
      return `On the ${ordinal(schedule.dayOfMonth)} of every month at ${time} ${zone}${suffix}`;
    }
    case "ONCE_AT_DATETIME":
      if (!isValidDateIso(schedule.runDate)) return "Once \u2014 no date picked yet";
      return `Once on ${schedule.runDate} at ${time} ${zone}`;
    default:
      return "";
  }
}

// src/metrics-dashboard.ts
var DAY = 864e5;
function addDays2(date, days) {
  const d = /* @__PURE__ */ new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
function mondayOf(date) {
  const d = /* @__PURE__ */ new Date(`${date}T00:00:00Z`);
  return addDays2(date, -((d.getUTCDay() + 6) % 7));
}
var weekOf = (at, timeZone) => mondayOf(localDateIn(at, timeZone));
var round3 = (n) => Math.round(n * 1e3) / 1e3;
var round1 = (n) => Math.round(n * 10) / 10;
function median(values) {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}
var mean = (values) => values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
function buildMetricsDashboard(input) {
  const tz = input.timeZone;
  const currentWeek = weekOf(input.now, tz);
  const selected = mondayOf(input.week) > currentWeek ? currentWeek : mondayOf(input.week);
  const previous = addDays2(selected, -7);
  const lastWeek = selected < currentWeek ? addDays2(selected, 7) : selected;
  const weeks = Array.from({ length: input.weekCount }, (_, i) => addDays2(lastWeek, -7 * (input.weekCount - 1 - i)));
  const automationById = new Map(input.automations.map((a) => [a.id, a]));
  const nameOf = (key) => input.people[key]?.name || input.people[key]?.email || "Someone";
  const runsByWeek = /* @__PURE__ */ new Map();
  for (const run of input.runs) {
    const key = weekOf(run.queuedAt, tz);
    runsByWeek.set(key, [...runsByWeek.get(key) ?? [], run]);
  }
  const runsIn = (week) => runsByWeek.get(week) ?? [];
  const activeIn = (week) => new Set(runsIn(week).map((r) => r.automationId).filter((id) => automationById.has(id)));
  const activeNow = activeIn(selected);
  const ownerCounts = /* @__PURE__ */ new Map();
  for (const id of activeNow) {
    const owner = automationById.get(id).createdById;
    ownerCounts.set(owner, (ownerCounts.get(owner) ?? 0) + 1);
  }
  const owners = new Set(input.automations.filter((a) => a.enabled).map((a) => a.createdById));
  const activeAutomations = {
    value: activeNow.size,
    previous: activeIn(previous).size,
    people: ownerCounts.size,
    series: weeks.map((week) => ({ week, value: activeIn(week).size })),
    perPerson: [...ownerCounts.entries()].map(([key, count]) => ({ key, name: nameOf(key), count })).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)),
    idle: [...owners].filter((o) => !ownerCounts.has(o)).map(nameOf).sort()
  };
  const month = addDays2(selected, 6).slice(0, 7);
  const monthOf = (at) => localDateIn(at, tz).slice(0, 7);
  const previousMonth = (m) => {
    const [y, mo] = m.split("-").map(Number);
    return mo === 1 ? `${y - 1}-12` : `${y}-${String(mo - 1).padStart(2, "0")}`;
  };
  const selfMade = input.automations.filter((a) => a.createdVia === "CONSOLE" || a.createdVia === "MCP");
  const months = [month];
  while (months.length < 6) months.unshift(previousMonth(months[0]));
  const upTo = (m) => selfMade.filter((a) => monthOf(a.createdAt) <= m);
  const selfPeople = /* @__PURE__ */ new Map();
  for (const a of upTo(month)) {
    const entry = selfPeople.get(a.createdById) ?? { total: 0, new: 0 };
    entry.total++;
    if (monthOf(a.createdAt) === month) entry.new++;
    selfPeople.set(a.createdById, entry);
  }
  const selfMadeCard = {
    month,
    allTime: upTo(month).length,
    previousMonthAllTime: upTo(previousMonth(month)).length,
    byOrigin: {
      console: upTo(month).filter((a) => a.createdVia === "CONSOLE").length,
      mcp: upTo(month).filter((a) => a.createdVia === "MCP").length
    },
    series: months.map((m) => ({ month: m, cumulative: upTo(m).length })),
    perPerson: [...selfPeople.entries()].map(([key, v]) => ({ key, name: nameOf(key), ...v })).sort((a, b) => b.total - a.total || a.name.localeCompare(b.name))
  };
  const classified = input.feedback.map((arrival) => ({ arrival, outcome: classifyArrival(arrival).outcome }));
  const counted = classified.filter((c) => c.outcome === "correction" || c.outcome === "update");
  const feedbackIn = (week) => counted.filter((c) => weekOf(c.arrival.at, tz) === week);
  const feedbackNow = feedbackIn(selected);
  const feedbackPeople = /* @__PURE__ */ new Map();
  for (const { arrival, outcome } of feedbackNow) {
    if (!arrival.person) continue;
    const entry = feedbackPeople.get(arrival.person.key) ?? {
      name: arrival.person.name || arrival.person.email || nameOf(arrival.person.key),
      corrections: 0,
      updates: 0
    };
    if (outcome === "correction") entry.corrections++;
    else entry.updates++;
    feedbackPeople.set(arrival.person.key, entry);
  }
  const everGave = /* @__PURE__ */ new Map();
  for (const { arrival } of counted) {
    if (arrival.person && weeks.includes(weekOf(arrival.at, tz))) {
      everGave.set(arrival.person.key, arrival.person.name || arrival.person.email || nameOf(arrival.person.key));
    }
  }
  const feedbackVolume = {
    corrections: feedbackNow.filter((c) => c.outcome === "correction").length,
    updates: feedbackNow.filter((c) => c.outcome === "update").length,
    total: feedbackNow.length,
    previousTotal: feedbackIn(previous).length,
    byChannel: {
      slack: feedbackNow.filter((c) => c.arrival.channel === "slack-chat").length,
      mcp: feedbackNow.filter((c) => c.arrival.channel === "mcp").length
    },
    series: weeks.map((week) => {
      const w = feedbackIn(week);
      return { week, corrections: w.filter((c) => c.outcome === "correction").length, updates: w.filter((c) => c.outcome === "update").length };
    }),
    perPerson: [...feedbackPeople.entries()].map(([key, v]) => ({ key, ...v })).sort((a, b) => b.corrections + b.updates - (a.corrections + a.updates) || a.name.localeCompare(b.name)),
    idle: [...everGave.entries()].filter(([key]) => !feedbackPeople.has(key)).map(([, name]) => name).sort()
  };
  const versionsByRun = /* @__PURE__ */ new Map();
  for (const v of input.docVersions) versionsByRun.set(v.runId, [...versionsByRun.get(v.runId) ?? [], v]);
  const editOf = (run) => {
    const human = (versionsByRun.get(run.id) ?? []).filter((v) => !v.byAutomation);
    if (human.length === 0) return null;
    return human.reduce((a, b) => b.modifiedTime > a.modifiedTime ? b : a).changedShare;
  };
  const editsIn = (week) => runsIn(week).filter((r) => r.driveFileId).map((r) => ({ run: r, share: editOf(r) }));
  const returnedIn = (week) => editsIn(week).filter((e) => e.share !== null);
  const medianOf = (week) => {
    const m = median(returnedIn(week).map((e) => e.share));
    return m === null ? null : round3(m);
  };
  const perAutomationEdits = /* @__PURE__ */ new Map();
  for (const { run, share } of returnedIn(selected)) {
    perAutomationEdits.set(run.automationId, [...perAutomationEdits.get(run.automationId) ?? [], share]);
  }
  const editShare = {
    median: medianOf(selected),
    previousMedian: medianOf(previous),
    returned: returnedIn(selected).length,
    tracked: runsIn(selected).filter((r) => versionsByRun.has(r.id)).length,
    series: weeks.map((week) => ({ week, median: medianOf(week), returned: returnedIn(week).length })),
    perAutomation: [...perAutomationEdits.entries()].map(([automationId, shares]) => ({
      automationId,
      name: automationById.get(automationId)?.name ?? "Deleted automation",
      returned: shares.length,
      median: round3(median(shares))
    })).sort((a, b) => b.median - a.median)
  };
  const correctedThreads = new Set(
    classified.filter((c) => c.arrival.channel === "slack-chat" && c.arrival.thread && (c.outcome === "correction" || c.outcome === "update")).map((c) => `${c.arrival.thread.channelId}|${c.arrival.thread.threadTs}`)
  );
  const outputsIn = (week) => runsIn(week).filter((r) => r.postedToSlack || r.driveFileId).map((r) => {
    const via = [];
    if (editOf(r) !== null) via.push("drive");
    if (r.slackChannelId && r.slackTs && correctedThreads.has(`${r.slackChannelId}|${r.slackTs}`)) via.push("slack");
    return { run: r, via };
  });
  const rateOf = (week) => {
    const outs = outputsIn(week);
    return outs.length ? round3(outs.filter((o) => o.via.length > 0).length / outs.length) : null;
  };
  const outputsNow = outputsIn(selected);
  const correctionRate = {
    rate: rateOf(selected),
    previousRate: rateOf(previous),
    corrected: outputsNow.filter((o) => o.via.length > 0).length,
    outputs: outputsNow.length,
    series: weeks.map((week) => ({ week, rate: rateOf(week), outputs: outputsIn(week).length })),
    items: outputsNow.map((o) => ({
      runId: o.run.id,
      automation: automationById.get(o.run.automationId)?.name ?? "Deleted automation",
      corrected: o.via.length > 0,
      via: o.via
    })).sort((a, b) => Number(b.corrected) - Number(a.corrected))
  };
  const runsByAutomation = /* @__PURE__ */ new Map();
  for (const run of input.runs) runsByAutomation.set(run.automationId, [...runsByAutomation.get(run.automationId) ?? [], run.queuedAt]);
  const cadenceOf = (a) => {
    if (a.trigger === "DAILY_AT_TIME" || a.trigger === "ONCE_AT_DATETIME") return null;
    if (a.trigger === "WEEKLY_ON_DAYS") return a.daysOfWeek.length ? 7 / a.daysOfWeek.length : 7;
    if (a.trigger === "MONTHLY_ON_DATE") return 30;
    const times = (runsByAutomation.get(a.id) ?? []).map((d) => d.getTime()).sort((x, y) => x - y);
    if (times.length < 3) return null;
    return median(times.slice(1).map((t, i) => (t - times[i]) / DAY));
  };
  const cadences = /* @__PURE__ */ new Map();
  let excluded = 0;
  for (const a of input.automations) {
    const cadence = cadenceOf(a);
    if (cadence !== null && cadence > 3) cadences.set(a.id, cadence);
    else excluded++;
  }
  const bucketOf = (days) => days <= 5 ? "twice-weekly" : days <= 10 ? "weekly" : "less-often";
  const LABELS = { "twice-weekly": "About twice a week", weekly: "Weekly", "less-often": "Less often than weekly" };
  const daysOld = (r) => r.freshness && r.freshness.surfaced.newestAgeHours !== null ? r.freshness.surfaced.newestAgeHours / 24 : null;
  const freshIn = (week) => runsIn(week).filter((r) => cadences.has(r.automationId));
  const groups = ["twice-weekly", "weekly", "less-often"].map((cadence) => {
    const inGroup = (r) => bucketOf(cadences.get(r.automationId)) === cadence;
    const now = freshIn(selected).filter(inGroup);
    const before = freshIn(previous).filter(inGroup);
    const ages = (rs) => rs.map(daysOld).filter((d) => d !== null);
    const intervals = [...cadences.entries()].filter(([, d]) => bucketOf(d) === cadence).map(([, d]) => d);
    const m = mean(ages(now));
    const p = mean(ages(before));
    return {
      cadence,
      label: LABELS[cadence],
      intervalDays: round1(median(intervals) ?? 0),
      runs: now.length,
      measured: ages(now).length,
      meanDaysOld: m === null ? null : round1(m),
      previousMeanDaysOld: p === null ? null : round1(p),
      hasAutomations: intervals.length > 0
    };
  }).filter((g) => g.hasAutomations).map(({ hasAutomations: _drop, ...g }) => g);
  const missedIn = (week) => freshIn(week).filter((r) => {
    const d = daysOld(r);
    return d !== null && d > cadences.get(r.automationId);
  });
  const freshness = {
    groups,
    missed: {
      count: missedIn(selected).length,
      of: freshIn(selected).filter((r) => daysOld(r) !== null).length,
      previousCount: missedIn(previous).length,
      runs: missedIn(selected).map((r) => ({
        runId: r.id,
        automation: automationById.get(r.automationId)?.name ?? "Deleted automation",
        date: localDateIn(r.queuedAt, tz),
        cadence: LABELS[bucketOf(cadences.get(r.automationId))],
        daysOld: round1(daysOld(r)),
        intervalDays: round1(cadences.get(r.automationId))
      })).sort((a, b) => b.daysOld - a.daysOld)
    },
    excluded
  };
  return {
    timeZone: tz,
    week: {
      start: selected,
      end: addDays2(selected, 6),
      isLatest: selected === currentWeek,
      previousStart: previous,
      nextStart: selected < currentWeek ? addDays2(selected, 7) : null
    },
    weeks,
    activeAutomations,
    selfMade: selfMadeCard,
    feedbackVolume,
    editShare,
    correctionRate,
    reviewVolume: null,
    freshness
  };
}

// src/playground.ts
var PLAYGROUND_AGENTS = [
  {
    agent: "INGEST_TRIAGE",
    label: "Ingest \xB7 placement",
    blurb: "Reads the whole project hierarchy and decides which projects an arrival belongs in, and which verbatim parts of it each one gets.",
    safety: "Every tool in this roster reads. The phase that writes pages runs after placement is settled and is never reached here, so a run reads the real hierarchy, the real pages and the real search index and leaves all three untouched. It takes no write lease either, so it can neither block nor be blocked by live ingestion.",
    prompts: {
      system: "kb_memory_ingest_triage_system",
      user: "kb_memory_ingest_triage_user"
    },
    input: "arrival",
    userPlaceholders: ["source", "itemCount", "projectCount", "items"],
    usesMinConfidence: true,
    tools: [
      {
        name: "ingest_projects",
        group: "gather",
        summary: "The projects this content could belong to, as a tree with each one\u2019s description and page count. The list the agent may choose between \u2014 it cannot add to it.",
        withoutIt: "The agent never sees the candidate list, so it can only name projects mentioned in the prompt or the content itself. Almost every assignment will be refused as an unknown project."
      },
      {
        name: "ingest_outline",
        group: "gather",
        summary: "One project\u2019s pages as an indented tree of paths, titles and summaries \u2014 how the agent tells what a codename actually covers.",
        withoutIt: "Placement falls back to project descriptions and search snippets alone. Worth trying: it isolates how much of the decision the generated description is really carrying."
      },
      {
        name: "ingest_search",
        group: "gather",
        summary: "Hybrid lexical + semantic search across every project at once \u2014 the strongest signal there is, since pages resembling the arrival cluster where it belongs.",
        withoutIt: "The agent decides from names, descriptions and outlines. This is the single most informative tool to remove if you want to know what the descriptions are worth on their own."
      },
      {
        name: "ingest_read",
        group: "gather",
        summary: 'Full page bodies, in any project \u2014 for when a snippet cannot separate "this continues that page" from "same vendor, different engagement".',
        withoutIt: "The agent must judge from snippets. Expect more confident-but-wrong placements between projects that share a vocabulary."
      },
      {
        name: "ingest_assign",
        group: "decide",
        summary: "Hands a quoted span of an item to a project, with a topic, a reason and a confidence. The only tool that places anything.",
        structural: true,
        withoutIt: "Nothing can be placed at all. The run will read, conclude, and end with the whole arrival falling to the organization root \u2014 which is a useful control, but not a placement."
      },
      {
        name: "ingest_discard",
        group: "decide",
        summary: "Marks a span as not worth keeping anywhere, with a stated reason. On the record, never silent.",
        withoutIt: "Pleasantries and scheduling chatter have nowhere to go but a project or the organization root. Watch whether the agent starts assigning them rather than leaving them unclaimed."
      },
      {
        name: "ingest_withhold",
        group: "decide",
        summary: "The guardrail: withholds a quoted passage in one of the sources hub\u2019s screening categories \u2014 a diagnosis, a home address, a credential \u2014 from every project, when the agent is confident. Cut out of every part that covers it, and logged.",
        withoutIt: "Nothing is withheld at placement. Sensitive passages reach the write phase, where the writer can still leave them out or flag them \u2014 this measures how much the placement-time guardrail is catching that writing would not."
      },
      {
        name: "ingest_review_plan",
        group: "check",
        summary: "What has been placed per project, what nobody has claimed, and coverage per item \u2014 where over 100% is the double-assignment bug.",
        withoutIt: "The agent loses its only way to notice it assigned the same sentences twice, or left a section behind. The coverage numbers on the run are measured independently, so you still see what it could not."
      },
      {
        name: "ingest_undo",
        group: "check",
        summary: "Removes one assignment by index \u2014 how the agent corrects itself after reviewing the plan.",
        withoutIt: "Every assignment is final once made. Pairs naturally with removing ingest_review_plan; on its own it tests whether the agent notices problems it then cannot fix."
      },
      {
        name: "ingest_confirm",
        group: "settle",
        summary: "Settles the placement. The loop\u2019s stop condition.",
        structural: true,
        withoutIt: "The loop can only end by exhausting its step budget. Whatever had been assigned by then is still used, so this measures how much of the work happens before the agent thinks it is finished."
      }
    ]
  },
  {
    agent: "KB_RETRIEVE",
    label: "Ask \xB7 retrieval",
    blurb: "Answers one question from the knowledge base \u2014 the loop behind kb_ask, the console\u2019s Ask KB and an automation\u2019s kb_ask \u2014 citing the pages it read.",
    safety: "Every tool in this roster reads, and the loop writes no row of its own (an ask record is written by the caller, which a rehearsal is not). It reads the real tree, pages, evidence and entity index across the organization and leaves all of it untouched.",
    prompts: {
      system: "kb_memory_search_orchestrator_system",
      user: "kb_memory_search_orchestrator_user"
    },
    input: "question",
    userPlaceholders: ["query"],
    usesMinConfidence: false,
    tools: [
      {
        name: "kb_outline",
        group: "gather",
        summary: "The tree of pages in view \u2014 a digest of each level once it is too large to send whole, expandable one branch at a time.",
        withoutIt: "The agent starts from search alone and cannot see what exists beside a hit. Isolates how much the map of the store is doing."
      },
      {
        name: "kb_search",
        group: "gather",
        summary: "Hybrid lexical + semantic search over sections, each hit naming the page and the section that matched.",
        withoutIt: "The agent must navigate by outline and grep. The most informative tool to remove if you want to know what ranking is worth."
      },
      {
        name: "kb_grep",
        group: "gather",
        summary: "Exact substring or pattern match across every page in view \u2014 names, codes, numbers.",
        withoutIt: "Exact strings must be found through search, which stems and ranks them away."
      },
      {
        name: "kb_read",
        group: "gather",
        summary: "A page, or one section of it by `path#anchor`, with the page\u2019s other anchors.",
        structural: true,
        withoutIt: "The agent answers from snippets. Citations to pages it never opened are refused, so expect the fallback."
      },
      {
        name: "kb_evidence",
        group: "gather",
        summary: "The verbatim source sentences a page or section rests on, with source name and date.",
        withoutIt: "Answers can cite pages but not the meeting or document a statement came from."
      },
      {
        name: "kb_page_history",
        group: "gather",
        summary: "A page\u2019s revisions \u2014 hand or agent, diffs, or the page as it stood on a date.",
        withoutIt: "\u201CWhat did we say before\u201D and \u201Cwhen did this change\u201D become unanswerable."
      },
      {
        name: "kb_entity",
        group: "gather",
        summary: "Everything recorded about one named person, organization or product: pages, dated claims, co-mentioned names.",
        withoutIt: "Questions about a person or company fall back to searching the name, which is top-k rather than complete."
      },
      {
        name: "kb_answer",
        group: "settle",
        summary: "Submits the answer with the pages (and optionally evidence) it cites. The loop\u2019s stop condition.",
        structural: true,
        withoutIt: "The loop can only end at its step cap, and every run is answered by the deterministic fallback \u2014 a measure of what the fallback alone produces."
      }
    ]
  }
];
var ALL_PLAYGROUND_AGENTS = PLAYGROUND_AGENTS.map((entry) => entry.agent);
function playgroundAgent(agent) {
  const found = PLAYGROUND_AGENTS.find((entry) => entry.agent === agent);
  if (!found) throw new Error(`No playground agent definition for "${agent}"`);
  return found;
}
function emptyPlaygroundSpec() {
  return {
    model: null,
    systemPrompt: null,
    userPrompt: null,
    maxSteps: null,
    minConfidence: null,
    reasoningEffort: null,
    tools: []
  };
}
function toolSettingOf(spec, name) {
  return spec.tools.find((entry) => entry.name === name) ?? { name, enabled: true };
}
function isBaselineSpec(spec) {
  return spec.model === null && spec.systemPrompt === null && spec.userPrompt === null && spec.maxSteps === null && spec.minConfidence === null && spec.reasoningEffort === null && spec.tools.every((tool) => tool.enabled && !tool.guidance?.trim());
}
function isRetrieveMetrics(metrics) {
  return !!metrics && typeof metrics === "object" && metrics.kind === "retrieve";
}
function emptyPlaygroundExpectation() {
  return {
    expectProjectIds: [],
    forbidProjectIds: [],
    exactProjects: false,
    minClaimedPct: null,
    maxAssignedPct: null,
    requireConfirmed: false,
    expectPaths: [],
    requireAnswered: false,
    notes: null
  };
}
function expectationAsserts(expectation) {
  if (!expectation) return false;
  return expectation.expectProjectIds.length > 0 || expectation.forbidProjectIds.length > 0 || expectation.minClaimedPct !== null || expectation.maxAssignedPct !== null || expectation.requireConfirmed || (expectation.expectPaths?.length ?? 0) > 0 || expectation.requireAnswered === true;
}
var LIVE_PLAYGROUND_STATUSES = ["QUEUED", "PROCESSING"];
function isPlaygroundRunLive(status) {
  return LIVE_PLAYGROUND_STATUSES.includes(status);
}

// src/prompt-cache.ts
var MAX_SESSION_CHARS = 256;
function promptCacheParams(ttl, session) {
  if (!ttl) return {};
  return {
    cacheControl: { type: "ephemeral", ttl },
    sessionId: session.slice(0, MAX_SESSION_CHARS)
  };
}
function parsePromptCacheTtl(raw, fallback) {
  const value = raw?.trim().toLowerCase();
  if (!value) return fallback;
  if (value === "off" || value === "false" || value === "none") return null;
  if (value === "5m" || value === "1h") return value;
  return fallback;
}

// src/routing-feedback.ts
var ROUTING_FEEDBACK_KINDS = [
  {
    kind: "WRONG_PROJECT",
    label: "Went to the wrong project",
    description: "A project received this that should not have.",
    stage: "placement",
    needsProject: true,
    needsSuggestion: true
  },
  {
    kind: "MISSED_PROJECT",
    label: "Missed a project",
    description: "A project that should have received this did not.",
    stage: "placement",
    needsSuggestion: true
  },
  {
    kind: "SHOULD_NOT_INGEST",
    label: "Should not have been absorbed at all",
    description: "Noise \u2014 nothing here was worth keeping in any knowledge base.",
    stage: "placement"
  },
  {
    kind: "BAD_SPLIT",
    label: "Split badly",
    description: "The source was cut into the wrong pieces, or should have been left whole.",
    stage: "placement"
  },
  {
    kind: "DUPLICATE_PAGE",
    label: "Created a page that already existed",
    description: "It wrote a new page instead of amending the one that already covered this. The fix is the search-before-create rule, not the destination.",
    stage: "writing",
    needsProject: true
  },
  {
    kind: "LOST_CONTENT",
    label: "Dropped something from the source",
    description: "The page it wrote leaves out something the source said that mattered.",
    stage: "writing",
    needsProject: true
  },
  {
    kind: "WRONG_CONTENT",
    label: "Wrote something the source does not say",
    description: "The page states something the source does not support \u2014 invented, or carried over from another project\u2019s material in the same batch.",
    stage: "writing",
    needsProject: true
  },
  {
    kind: "GOOD",
    label: "This was right",
    description: "Filed where it belongs and written up correctly. Worth recording \u2014 otherwise only mistakes are ever counted.",
    stage: "either",
    positive: true
  },
  {
    kind: "OTHER",
    label: "Something else",
    description: "Anything the kinds above do not cover. Say what in the note.",
    stage: "either"
  }
];
var ALL_ROUTING_FEEDBACK_KINDS = ROUTING_FEEDBACK_KINDS.map((k) => k.kind);
function routingFeedbackKind(kind) {
  return ROUTING_FEEDBACK_KINDS.find((k) => k.kind === kind) ?? ROUTING_FEEDBACK_KINDS[ROUTING_FEEDBACK_KINDS.length - 1];
}
var ROUTING_FEEDBACK_STATUSES = [
  { status: "OPEN", label: "Open", description: "Nobody has looked at this yet.", open: true },
  {
    status: "ACKNOWLEDGED",
    label: "Acknowledged",
    description: "Read, and accepted as a real problem. Not fixed yet.",
    open: true
  },
  {
    status: "ACTIONED",
    label: "Actioned",
    description: "Something changed because of it \u2014 a description rewritten, a prompt tuned, content re-filed."
  },
  {
    status: "DISMISSED",
    label: "Dismissed",
    description: "Not a problem, or not one worth acting on. The note should say why."
  }
];
var ALL_ROUTING_FEEDBACK_STATUSES = ROUTING_FEEDBACK_STATUSES.map(
  (s) => s.status
);
function routingFeedbackStatus(status) {
  return ROUTING_FEEDBACK_STATUSES.find((s) => s.status === status) ?? ROUTING_FEEDBACK_STATUSES[0];
}
var OPEN_ROUTING_FEEDBACK_STATUSES = ROUTING_FEEDBACK_STATUSES.filter(
  (s) => s.open
).map((s) => s.status);

// src/run-events.ts
var NOOP_RUN_EVENT_SINK = {
  emit() {
  },
  async flush() {
  },
  async close() {
  }
};
function isDurableRunEvent(event) {
  if (event.type === "thought") return event.done;
  if (event.type === "tool") return event.state !== "running";
  return true;
}
function isTerminalPhase(phase) {
  return phase === "done" || phase === "failed";
}
function emptyRunTimeline(phase = "queued") {
  return { activities: [], phase, activity: null, chunk: 0, chunkCount: null, error: null, cursor: 0 };
}
function foldRunEvent(timeline, event) {
  const next = { ...timeline, activities: timeline.activities };
  if (event.seq > next.cursor) next.cursor = event.seq;
  if (event.chunk > next.chunk) next.chunk = event.chunk;
  if (event.type === "status") {
    next.phase = event.phase;
    next.activity = event.activity;
    if (event.chunkCount !== null) next.chunkCount = event.chunkCount;
    if (event.error) next.error = event.error;
    return next;
  }
  const activities = [...next.activities];
  const key = event.type === "milestone" ? `m:${event.seq}` : `${event.type}:${event.id}`;
  const index = activities.findIndex((a) => a.key === key);
  if (event.type === "thought") {
    const existing = index >= 0 && activities[index].kind === "thought" ? activities[index] : null;
    const text = event.text !== void 0 ? event.text : existing ? event.seq <= timeline.cursor ? existing.text : existing.text + (event.delta ?? "") : event.delta ?? "";
    const folded = {
      key,
      seq: existing?.seq ?? event.seq,
      at: existing?.at ?? event.at,
      chunk: event.chunk,
      kind: "thought",
      thoughtKind: event.kind,
      text,
      done: event.done
    };
    if (index >= 0) activities[index] = folded;
    else activities.push(folded);
  } else if (event.type === "tool") {
    const existing = index >= 0 && activities[index].kind === "tool" ? activities[index] : null;
    const folded = {
      key,
      seq: existing?.seq ?? event.seq,
      at: existing?.at ?? event.at,
      chunk: event.chunk,
      kind: "tool",
      tool: event.tool,
      state: event.state,
      // A resolved call must not lose the arguments the running event carried, and a late
      // duplicate of the running event must not blank the output it already has.
      input: event.input !== void 0 ? event.input : existing?.input,
      output: event.output !== void 0 ? event.output : existing?.output,
      ms: event.ms ?? existing?.ms
    };
    if (index >= 0) activities[index] = folded;
    else activities.push(folded);
  } else {
    const folded = {
      key,
      seq: event.seq,
      at: event.at,
      chunk: event.chunk,
      kind: "milestone",
      label: event.label,
      detail: event.detail,
      tone: event.tone ?? "good"
    };
    if (index >= 0) activities[index] = folded;
    else activities.push(folded);
  }
  next.activities = activities;
  return next;
}
function foldRunEvents(timeline, events) {
  return events.reduce(foldRunEvent, timeline);
}
function phaseForTool(tool) {
  if (tool === "finish") return "writing";
  if (tool === "slack_post_message" || tool === "kb_save_note") return "delivering";
  if (tool === "sweep_finish") return "writing";
  if (tool === "sweep_flag" || tool === "sweep_resolve") return "deciding";
  if (tool.startsWith("sweep_")) return "reading";
  if (tool.startsWith("ingest_")) return "triaging";
  if (tool.startsWith("kb_commit")) return "committing";
  if (tool.startsWith("kb_stage") || tool.startsWith("stage") || tool.startsWith("kb_review_plan") || tool.startsWith("kb_discard")) return "deciding";
  if (tool.startsWith("kb_outline") || tool.startsWith("kb_search") || tool.startsWith("kb_read") || tool.startsWith("kb_grep") || tool.startsWith("kb_ask") || tool.startsWith("kb_evidence") || tool.startsWith("kb_page_history") || tool.startsWith("kb_entity") || tool.startsWith("granola_") || tool.startsWith("slack_list") || tool === "automation_previous_runs" || tool === "current_datetime") {
    return "reading";
  }
  return "thinking";
}
var RUN_PHASE_LABEL = {
  queued: "Queued",
  starting: "Starting up",
  thinking: "Thinking",
  reading: "Gathering context",
  triaging: "Deciding where this belongs",
  deciding: "Deciding what to change",
  committing: "Applying changes",
  writing: "Writing the document",
  delivering: "Delivering",
  done: "Finished",
  failed: "Failed"
};

// src/run-usage.ts
function emptyRunUsage() {
  return { costUsd: 0, inputTokens: 0, outputTokens: 0, cachedTokens: 0, reasoningTokens: 0, modelCalls: 0 };
}
function addRunUsage(a, b) {
  return {
    costUsd: a.costUsd + b.costUsd,
    inputTokens: a.inputTokens + b.inputTokens,
    outputTokens: a.outputTokens + b.outputTokens,
    cachedTokens: a.cachedTokens + b.cachedTokens,
    reasoningTokens: a.reasoningTokens + b.reasoningTokens,
    modelCalls: a.modelCalls + b.modelCalls
  };
}
function runUsageTokens(usage) {
  return usage.inputTokens + usage.outputTokens;
}
function formatRunCost(costUsd) {
  if (!Number.isFinite(costUsd) || costUsd <= 0) return "$0";
  if (costUsd < 0.01) return `${(costUsd * 100).toFixed(costUsd < 1e-3 ? 2 : 1)}\xA2`;
  if (costUsd < 1) return `${(costUsd * 100).toFixed(1)}\xA2`;
  return `$${costUsd.toFixed(2)}`;
}
function formatRunTokens(tokens) {
  if (!Number.isFinite(tokens) || tokens <= 0) return "0";
  if (tokens < 1e3) return String(Math.round(tokens));
  if (tokens < 1e6) return `${(tokens / 1e3).toFixed(tokens < 1e4 ? 1 : 0)}k`;
  return `${(tokens / 1e6).toFixed(1)}M`;
}

// src/ingest-window.ts
var INGEST_INTERVALS = [
  { minutes: 15, label: "Every 15 minutes" },
  { minutes: 30, label: "Every 30 minutes" },
  { minutes: 60, label: "Every hour" },
  { minutes: 120, label: "Every 2 hours" },
  { minutes: 180, label: "Every 3 hours" },
  { minutes: 240, label: "Every 4 hours" },
  { minutes: 360, label: "Every 6 hours" },
  { minutes: 480, label: "Every 8 hours" },
  { minutes: 720, label: "Every 12 hours" }
];
var INGEST_INTERVAL_MINUTES = INGEST_INTERVALS.map((interval) => interval.minutes);
var DAY_MINUTES = 24 * 60;
function validateIngestWindow(window) {
  const daily = validateSchedule({ trigger: "DAILY_AT_TIME", timeOfDay: window.timeOfDay, timezone: window.timezone ?? "UTC" });
  if (daily) return daily.replace("for a scheduled automation", "for a batch window");
  if (window.everyMinutes != null && !INGEST_INTERVAL_MINUTES.includes(window.everyMinutes)) {
    return `The interval must be one of ${INGEST_INTERVAL_MINUTES.join(", ")} minutes`;
  }
  return null;
}
function clock(minuteOfDay) {
  return `${String(Math.floor(minuteOfDay / 60)).padStart(2, "0")}:${String(minuteOfDay % 60).padStart(2, "0")}`;
}
function minutesOf(timeOfDay) {
  const [h, m] = timeOfDay.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}
function ingestWindowSlots(window) {
  const anchor = minutesOf(window.timeOfDay ?? "00:00");
  const every = window.everyMinutes ?? DAY_MINUTES;
  const first = anchor % every;
  const slots = [];
  for (let at = first; at < DAY_MINUTES; at += every) slots.push(clock(at));
  return slots;
}
function addDays3(dateIso, days) {
  const d = /* @__PURE__ */ new Date(`${dateIso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
function candidates(window, now) {
  const zone = safeTimeZone(window.timezone);
  const today = localDateIn(now, zone);
  const slots = ingestWindowSlots(window);
  const out = [];
  for (const offset of [-1, 0, 1]) {
    const date = addDays3(today, offset);
    for (const slot of slots) out.push(zonedInstant(date, slot, zone));
  }
  return out.sort((a, b) => a.getTime() - b.getTime());
}
function previousIngestWindow(window, now = /* @__PURE__ */ new Date()) {
  if (validateIngestWindow(window) !== null) return null;
  let latest = null;
  for (const at of candidates(window, now)) if (at <= now) latest = at;
  return latest;
}
function nextIngestWindow(window, now = /* @__PURE__ */ new Date()) {
  if (validateIngestWindow(window) !== null) return null;
  return candidates(window, now).find((at) => at > now) ?? null;
}
function describeIngestWindow(window) {
  const time = window.timeOfDay ?? "\u2014";
  if (window.everyMinutes == null) return `Daily at ${time}`;
  const label = INGEST_INTERVALS.find((interval) => interval.minutes === window.everyMinutes)?.label ?? `Every ${window.everyMinutes} minutes`;
  return `${label} from ${time}`;
}

// src/ingest-rules.ts
var INGEST_RULE_SOURCE_TYPES = ["EMAIL", "SLACK", "GRANOLA", "DRIVE_FILE", "LINKED_DOCUMENT"];
var INGEST_RULE_SOURCE_LABELS = {
  EMAIL: "Email",
  SLACK: "Slack",
  GRANOLA: "Meetings",
  DRIVE_FILE: "Drive files",
  LINKED_DOCUMENT: "Linked documents"
};
function isRuledSourceType(sourceType) {
  return INGEST_RULE_SOURCE_TYPES.includes(sourceType);
}
var INGEST_MAIL_CLASSES = ["person", "auto-reply", "calendar", "group", "bulk", "automated"];
var INGEST_MAIL_CLASS_LABELS = {
  person: "Person",
  "auto-reply": "Auto-reply",
  calendar: "Calendar",
  group: "Group",
  bulk: "Bulk",
  automated: "Automated"
};
var INGEST_RULE_LIMITS = {
  rules: 40,
  questions: 20,
  contextChars: 2e3,
  nameChars: 120,
  questionChars: 500,
  criteriaChars: 500,
  conditionValues: 50,
  conditionChars: 200
};
var DEFAULT_INGEST_RULE_THRESHOLD = 0.8;
var EMPTY_INGEST_POLICY = { context: "", defaultEffect: "allow", rules: [] };
function isEmptyIngestPolicy(policy) {
  return policy.rules.length === 0 && policy.defaultEffect === "allow";
}
function normalizeIngestPolicy(input) {
  const problems = [];
  const body = isObject(input) ? input : {};
  if (!isObject(input)) problems.push("The policy must be an object");
  const context = typeof body.context === "string" ? body.context.trim() : "";
  if (body.context !== void 0 && body.context !== null && typeof body.context !== "string") problems.push("`context` must be a string");
  if (context.length > INGEST_RULE_LIMITS.contextChars) problems.push(`\`context\` may be at most ${INGEST_RULE_LIMITS.contextChars} characters`);
  const defaultEffect = body.defaultEffect ?? "allow";
  if (defaultEffect !== "allow" && defaultEffect !== "block") problems.push('`defaultEffect` must be "allow" or "block"');
  const rawRules = body.rules ?? [];
  if (!Array.isArray(rawRules)) problems.push("`rules` must be a list");
  const list = Array.isArray(rawRules) ? rawRules : [];
  if (list.length > INGEST_RULE_LIMITS.rules) problems.push(`At most ${INGEST_RULE_LIMITS.rules} rules`);
  const used = /* @__PURE__ */ new Set();
  const rules = [];
  list.forEach((raw, index) => {
    const at = `Rule ${index + 1}`;
    if (!isObject(raw)) {
      problems.push(`${at} must be an object`);
      return;
    }
    const name = typeof raw.name === "string" ? raw.name.trim() : "";
    if (!name) problems.push(`${at} needs a name`);
    if (name.length > INGEST_RULE_LIMITS.nameChars) problems.push(`${at}: the name may be at most ${INGEST_RULE_LIMITS.nameChars} characters`);
    if (raw.effect !== "allow" && raw.effect !== "block") problems.push(`${at}: effect must be "allow" or "block"`);
    const when = normalizeConditions(raw.when, at, problems);
    const ask = normalizeQuestion(raw.ask, at, problems);
    let id = typeof raw.id === "string" && /^[a-z0-9]{1,24}$/.test(raw.id) ? raw.id : "";
    if (!id || used.has(id)) id = freshId(used);
    used.add(id);
    rules.push({ id, name, effect: raw.effect === "allow" ? "allow" : "block", when, ask });
  });
  const questions = rules.filter((rule) => rule.ask).length;
  if (questions > INGEST_RULE_LIMITS.questions) {
    problems.push(`At most ${INGEST_RULE_LIMITS.questions} rules may ask a question (this one has ${questions}); the rest must be decided by conditions`);
  }
  if (problems.length > 0) return { ok: false, problems };
  return { ok: true, value: { context, defaultEffect, rules } };
}
function readIngestPolicy(value) {
  const result = normalizeIngestPolicy(value);
  return result.ok ? result.value : EMPTY_INGEST_POLICY;
}
function normalizeConditions(raw, at, problems) {
  if (raw === void 0 || raw === null) return {};
  if (!isObject(raw)) {
    problems.push(`${at}: conditions must be an object`);
    return {};
  }
  const out = {};
  const strings = (key, clean = (v) => v.trim()) => {
    const value = raw[key];
    if (value === void 0 || value === null) return;
    if (!Array.isArray(value) || value.some((item) => typeof item !== "string")) {
      problems.push(`${at}: ${key} must be a list of strings`);
      return;
    }
    const cleaned = [...new Set(value.map(clean).filter(Boolean))];
    if (cleaned.length > INGEST_RULE_LIMITS.conditionValues) problems.push(`${at}: ${key} may hold at most ${INGEST_RULE_LIMITS.conditionValues} values`);
    if (cleaned.some((item) => item.length > INGEST_RULE_LIMITS.conditionChars)) problems.push(`${at}: ${key} values may be at most ${INGEST_RULE_LIMITS.conditionChars} characters`);
    if (cleaned.length > 0) out[key] = cleaned;
  };
  strings("sourceTypes", (v) => v.trim().toUpperCase());
  strings("sources");
  strings("senderDomains", (v) => v.trim().toLowerCase().replace(/^@/, ""));
  strings("titleContains");
  strings("labels");
  strings("senders", (v) => v.trim().toLowerCase());
  strings("recipients", (v) => v.trim().toLowerCase());
  strings("mailClasses", (v) => v.trim().toLowerCase());
  strings("directions", (v) => v.trim().toLowerCase());
  for (const type of out.sourceTypes ?? []) {
    if (!isRuledSourceType(type)) problems.push(`${at}: rules do not apply to "${type}" \u2014 only ${INGEST_RULE_SOURCE_TYPES.join(", ")}`);
  }
  for (const mailClass of out.mailClasses ?? []) {
    if (!INGEST_MAIL_CLASSES.includes(mailClass)) problems.push(`${at}: unknown kind of mail "${mailClass}"`);
  }
  for (const direction of out.directions ?? []) {
    if (direction !== "inbound" && direction !== "sent") problems.push(`${at}: direction "${direction}" is neither inbound nor sent`);
  }
  for (const pattern of [...out.senders ?? [], ...out.recipients ?? []]) {
    if (!pattern.includes("@") && !pattern.includes("*")) problems.push(`${at}: "${pattern}" is neither an address nor a * pattern \u2014 a domain goes in sender domains`);
  }
  if (raw.hasAttachment !== void 0 && raw.hasAttachment !== null) {
    if (typeof raw.hasAttachment !== "boolean") problems.push(`${at}: hasAttachment must be true or false`);
    else out.hasAttachment = raw.hasAttachment;
  }
  return out;
}
function normalizeQuestion(raw, at, problems) {
  if (raw === void 0 || raw === null) return null;
  if (!isObject(raw)) {
    problems.push(`${at}: the question must be an object`);
    return null;
  }
  const question = typeof raw.question === "string" ? raw.question.trim() : "";
  if (!question) return null;
  if (question.length > INGEST_RULE_LIMITS.questionChars) problems.push(`${at}: the question may be at most ${INGEST_RULE_LIMITS.questionChars} characters`);
  const text = (key) => {
    const value = raw[key];
    if (value === void 0 || value === null) return void 0;
    if (typeof value !== "string") {
      problems.push(`${at}: ${key} must be a string`);
      return void 0;
    }
    const trimmed = value.trim();
    if (trimmed.length > INGEST_RULE_LIMITS.criteriaChars) problems.push(`${at}: ${key} may be at most ${INGEST_RULE_LIMITS.criteriaChars} characters`);
    return trimmed || void 0;
  };
  const threshold = raw.threshold === void 0 || raw.threshold === null ? DEFAULT_INGEST_RULE_THRESHOLD : raw.threshold;
  if (typeof threshold !== "number" || !Number.isFinite(threshold) || threshold < 0.5 || threshold > 0.99) {
    problems.push(`${at}: the threshold must be a number from 0.5 to 0.99`);
  }
  const yes = text("yes");
  const no = text("no");
  return {
    question,
    ...yes ? { yes } : {},
    ...no ? { no } : {},
    threshold: typeof threshold === "number" ? threshold : DEFAULT_INGEST_RULE_THRESHOLD
  };
}
function freshId(used) {
  for (let n = used.size + 1; ; n++) {
    const id = `r${n}`;
    if (!used.has(id)) return id;
  }
}
function ingestItemFacts(input) {
  const meta = isObject(input.metadata) ? input.metadata : {};
  const attached = isObject(meta.attachedTo) ? meta.attachedTo : {};
  const firstString = (...values) => values.find((v) => typeof v === "string" && v.trim().length > 0) ?? "";
  const labels = Array.isArray(meta.labelIds) ? meta.labelIds.filter((v) => typeof v === "string") : [];
  return {
    sourceType: input.sourceType,
    source: input.source,
    senderDomain: senderDomainOf(meta.from ?? attached.from),
    title: firstString(input.label, meta.title, meta.subject, meta.name, meta.filename),
    labels,
    mail: mailFactsOf(input.sourceType, meta, attached, labels)
  };
}
function mailFactsOf(sourceType, meta, attached, labels) {
  const fromEmail = (value) => {
    const address = isObject(value) && typeof value.email === "string" ? value.email : typeof value === "string" ? value.match(/<([^>]+)>/)?.[1] ?? value : null;
    return address?.includes("@") ? address.trim().toLowerCase() : null;
  };
  const list = (value) => Array.isArray(value) ? value.map(fromEmail).filter((e) => e !== null) : [];
  const isEmail = sourceType === "EMAIL";
  const carried = sourceType === "LINKED_DOCUMENT" && Object.keys(attached).length > 0;
  if (!isEmail && !carried) return null;
  const source = isEmail ? meta : attached;
  const from = fromEmail(source.from);
  const mailbox = typeof source.mailbox === "string" ? source.mailbox.toLowerCase() : null;
  return {
    from,
    recipients: [...list(source.to), ...list(source.cc)],
    mailClass: typeof source.mailClass === "string" ? source.mailClass : null,
    direction: labels.includes("SENT") || from !== null && from === mailbox ? "sent" : "inbound",
    // A document is itself the attachment; for an email, what it listed.
    hasAttachment: isEmail ? Array.isArray(meta.attachments) && meta.attachments.length > 0 : true
  };
}
function addressMatches(pattern, address) {
  const p = pattern.toLowerCase();
  if (!p.includes("*")) return p === address.toLowerCase();
  const re = new RegExp(`^${p.split("*").map((part) => part.replace(/[.+?^${}()|[\]\\]/g, "\\$&")).join(".*")}$`);
  return re.test(address.toLowerCase());
}
function senderDomainOf(from) {
  const address = isObject(from) && typeof from.email === "string" ? from.email : typeof from === "string" ? from.match(/<([^>]+)>/)?.[1] ?? from : null;
  if (!address) return null;
  const at = address.lastIndexOf("@");
  return at > 0 ? address.slice(at + 1).trim().toLowerCase() || null : null;
}
function ingestConditionsMatch(when, facts) {
  if (when.sourceTypes?.length && !when.sourceTypes.includes(facts.sourceType)) return false;
  if (when.sources?.length && !when.sources.some((s) => facts.source === s || facts.source.startsWith(`${s}:`))) return false;
  if (when.senderDomains?.length) {
    const domain = facts.senderDomain;
    if (!domain || !when.senderDomains.some((d) => domain === d || domain.endsWith(`.${d}`))) return false;
  }
  if (when.titleContains?.length) {
    const title = facts.title.toLowerCase();
    if (!when.titleContains.some((part) => title.includes(part.toLowerCase()))) return false;
  }
  if (when.labels?.length && !when.labels.some((label) => facts.labels.includes(label))) return false;
  if (when.senders?.length || when.recipients?.length || when.mailClasses?.length || when.directions?.length || when.hasAttachment !== void 0) {
    const mail = facts.mail;
    if (!mail) return false;
    if (when.senders?.length && !(mail.from && when.senders.some((p) => addressMatches(p, mail.from)))) return false;
    if (when.recipients?.length && !mail.recipients.some((r) => when.recipients.some((p) => addressMatches(p, r)))) return false;
    if (when.mailClasses?.length && !(mail.mailClass && when.mailClasses.includes(mail.mailClass))) return false;
    if (when.directions?.length && !when.directions.includes(mail.direction)) return false;
    if (when.hasAttachment !== void 0 && when.hasAttachment !== mail.hasAttachment) return false;
  }
  return true;
}
function applicableIngestRules(policy, facts) {
  const out = [];
  for (const rule of policy.rules) {
    if (!ingestConditionsMatch(rule.when, facts)) continue;
    out.push(rule);
    if (!rule.ask) break;
  }
  return out;
}
function decideIngestRules(policy, facts, answerOf) {
  for (const rule of applicableIngestRules(policy, facts)) {
    if (!rule.ask) return verdictOf(rule, null);
    const p = answerOf?.(rule);
    if (typeof p !== "number") {
      return { status: "UNANSWERED", reason: "rules-unanswered", ruleId: rule.id, ruleName: rule.name, probability: null };
    }
    if (p >= rule.ask.threshold) return verdictOf(rule, p);
  }
  return {
    status: policy.defaultEffect === "allow" ? "PASSED" : "BLOCKED",
    reason: "default",
    ruleId: null,
    ruleName: null,
    probability: null
  };
}
function verdictOf(rule, probability) {
  return { status: rule.effect === "allow" ? "PASSED" : "BLOCKED", reason: "rule", ruleId: rule.id, ruleName: rule.name, probability };
}
function ingestJevQuestion(context, ask) {
  const lead = context ? `${context.trim()}

` : "";
  return {
    type: "noul",
    instructions: `${lead}${ask.question}`,
    criteria: {
      true: ask.yes ?? `Yes: ${ask.question}`,
      false: ask.no ?? `No. The content does not match: ${ask.question}`
    }
  };
}
function describeIngestConditions(when) {
  const chips = [];
  if (when.sourceTypes?.length) chips.push(when.sourceTypes.map((t) => INGEST_RULE_SOURCE_LABELS[t] ?? t).join(" or "));
  if (when.sources?.length) chips.push(`from ${when.sources.join(" or ")}`);
  if (when.senderDomains?.length) chips.push(`sender @${when.senderDomains.join(" or @")}`);
  if (when.titleContains?.length) chips.push(`title has ${when.titleContains.map((t) => `\u201C${t}\u201D`).join(" or ")}`);
  if (when.labels?.length) chips.push(`label ${when.labels.join(" or ")}`);
  if (when.senders?.length) chips.push(`sent by ${when.senders.join(" or ")}`);
  if (when.recipients?.length) chips.push(`to ${when.recipients.join(" or ")}`);
  if (when.mailClasses?.length) chips.push(when.mailClasses.map((c) => INGEST_MAIL_CLASS_LABELS[c] ?? c).join(" or ") + " mail");
  if (when.directions?.length) chips.push(when.directions.join(" or "));
  if (when.hasAttachment !== void 0) chips.push(when.hasAttachment ? "with an attachment" : "without attachments");
  return chips;
}
function describeIngestRules(policy) {
  if (isEmptyIngestPolicy(policy)) return null;
  const named = (effect) => policy.rules.filter((rule) => rule.effect === effect).map((rule) => rule.ask ? `${rule.name} (judged by a question)` : rule.name);
  const blocks = named("block");
  const allows = named("allow");
  if (policy.defaultEffect === "allow") {
    return [`Blocks: ${blocks.join("; ") || "nothing"}`, allows.length ? `except, first: ${allows.join("; ")}` : ""].filter(Boolean).join(" \u2014 ");
  }
  return [`Accepts only: ${allows.join("; ") || "nothing"}`, blocks.length ? `refusing first: ${blocks.join("; ")}` : ""].filter(Boolean).join(" \u2014 ");
}
function isObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// src/sweeps.ts
var KB_SWEEP_FINDING_KINDS = [
  "MISPLACED",
  "DUPLICATE",
  "NEAR_DUPLICATE",
  "CONTRADICTION",
  "STRUCTURE",
  "STALE",
  "OTHER"
];
var KB_SWEEP_FINDING_KIND_LABELS = {
  MISPLACED: "Misplaced",
  DUPLICATE: "Duplicate",
  NEAR_DUPLICATE: "Near-duplicate",
  CONTRADICTION: "Contradiction",
  STRUCTURE: "Structure",
  STALE: "Stale",
  OTHER: "Other"
};
var KB_SWEEP_FINDING_KIND_DESCRIPTIONS = {
  MISPLACED: "A page or section filed in the wrong project or the wrong branch \u2014 it belongs somewhere else in the tree. Always HIGH.",
  DUPLICATE: "Two pages state the same knowledge; one should absorb the other.",
  NEAR_DUPLICATE: "Two pages mostly state the same knowledge, with differences worth reconciling before merging.",
  CONTRADICTION: "Two pages disagree about a fact that should have one answer.",
  STRUCTURE: "The shape of the tree is wrong: a missing index page, an oversize page that should be split, a thin stub, an orphaned section, a branch mirroring another.",
  STALE: "Superseded knowledge still stated as current.",
  OTHER: "Anything else worth a person's attention that the kinds above do not name."
};
var KB_SWEEP_SEVERITIES = ["HIGH", "MEDIUM", "LOW"];
var KB_SWEEP_SEVERITY_LABELS = { HIGH: "High", MEDIUM: "Medium", LOW: "Low" };
function sweepSeverityRank(severity) {
  return severity === "HIGH" ? 0 : severity === "MEDIUM" ? 1 : 2;
}
function sweepSeverityFor(kind, requested) {
  return kind === "MISPLACED" ? "HIGH" : requested;
}
var KB_SWEEP_FINDING_STATUSES = ["OPEN", "FIXING", "FIXED", "RESOLVED", "DISMISSED"];
var KB_SWEEP_FINDING_STATUS_LABELS = {
  OPEN: "Open",
  FIXING: "Fixing",
  FIXED: "Fixed",
  RESOLVED: "Resolved",
  DISMISSED: "Dismissed"
};
function isLiveSweepFindingStatus(status) {
  return status === "OPEN" || status === "FIXING";
}
function sweepFindingShortId(fingerprint) {
  return `SF-${fingerprint.slice(0, 6)}`;
}
var KB_SWEEP_TOOL_NAMES = [
  "sweep_projects",
  "sweep_outline",
  "sweep_search",
  "sweep_grep",
  "sweep_read",
  "sweep_similar_pairs",
  "sweep_neighbours",
  "sweep_compare",
  "sweep_shared_evidence",
  "sweep_open_findings",
  "sweep_flag",
  "sweep_resolve",
  "sweep_finish"
];

// src/spaces.ts
var SPACE_ACTIVITY_ACTIONS = [
  "space.created",
  "folder.created",
  "folder.renamed",
  "folder.moved",
  "folder.trashed",
  "folder.restored",
  "file.uploaded",
  "file.written",
  "file.renamed",
  "file.moved",
  "file.trashed",
  "file.restored",
  "grant.added",
  "grant.changed",
  "grant.removed",
  /** A share change asked for over MCP, waiting for the owner (Phase 4). */
  "share.requested",
  "share.approved",
  "share.declined"
];
function spaceBaseType(mimeType) {
  return (mimeType ?? "").split(";")[0].trim().toLowerCase();
}
function isSpaceTextType(mimeType) {
  const type = spaceBaseType(mimeType);
  return type.startsWith("text/") || type === "application/json" || type.endsWith("+json") || type === "application/xml" || type.endsWith("+xml") || type === "application/x-yaml" || type === "application/yaml";
}
var TEXT_EXTENSIONS = /* @__PURE__ */ new Set([
  "md",
  "markdown",
  "txt",
  "text",
  "csv",
  "tsv",
  "json",
  "jsonl",
  "ndjson",
  "yaml",
  "yml",
  "xml",
  "toml",
  "ini",
  "cfg",
  "conf",
  "log",
  "html",
  "htm",
  "css",
  "scss",
  "less",
  "svg",
  "tex",
  "rst",
  "adoc",
  "org",
  "js",
  "jsx",
  "mjs",
  "cjs",
  "ts",
  "tsx",
  "mts",
  "cts",
  "vue",
  "svelte",
  "py",
  "rb",
  "go",
  "rs",
  "java",
  "kt",
  "kts",
  "scala",
  "swift",
  "c",
  "h",
  "cc",
  "cpp",
  "hpp",
  "cs",
  "php",
  "lua",
  "pl",
  "r",
  "ex",
  "exs",
  "erl",
  "hs",
  "clj",
  "dart",
  "sql",
  "graphql",
  "gql",
  "proto",
  "sh",
  "bash",
  "zsh",
  "fish",
  "ps1",
  "bat",
  "dockerfile",
  "tf",
  "hcl",
  "gradle",
  "properties"
]);
var MARKDOWN_EXTENSIONS = /* @__PURE__ */ new Set(["md", "markdown"]);
var DOCUMENTS = [
  { extensions: ["pdf"], type: "application/pdf", markdown: false },
  { extensions: ["docx"], type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", markdown: false },
  { extensions: ["pptx"], type: "application/vnd.openxmlformats-officedocument.presentationml.presentation", markdown: true },
  { extensions: ["xlsx", "xlsm"], type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", markdown: true }
];
var LEGACY = [
  { extensions: ["doc"], type: "application/msword", reason: "Legacy Word files cannot be read; re-save it as .docx or .pdf to make it searchable" },
  { extensions: ["ppt"], type: "application/vnd.ms-powerpoint", reason: "Legacy PowerPoint files cannot be read; re-save it as .pptx or .pdf to make it searchable" },
  { extensions: ["xls"], type: "application/vnd.ms-excel", reason: "Legacy Excel files cannot be read; re-save it as .xlsx or .csv to make it searchable" }
];
function extensionOf(name) {
  const base = name.slice(name.lastIndexOf("/") + 1);
  if (base.toLowerCase() === "dockerfile") return "dockerfile";
  const dot = base.lastIndexOf(".");
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : "";
}
function spaceContentOf(name, mimeType) {
  const ext = extensionOf(name);
  const type = spaceBaseType(mimeType);
  const document = DOCUMENTS.find((d) => d.extensions.includes(ext) || d.type === type);
  if (document) return { kind: "document", markdown: document.markdown };
  const legacy = LEGACY.find((l) => l.extensions.includes(ext) || l.type === type);
  if (legacy) return { kind: "unsupported", markdown: false, reason: legacy.reason };
  if (TEXT_EXTENSIONS.has(ext) || isSpaceTextType(type)) {
    return { kind: "text", markdown: MARKDOWN_EXTENSIONS.has(ext) || type === "text/markdown" || type === "text/x-markdown" };
  }
  return { kind: "unsupported", markdown: false, reason: `No text can be read from ${type || "this type of"} files; it is found by name only` };
}

// src/slack.ts
function slackDestinationKind(id) {
  const trimmed = id.trim().toUpperCase();
  if (/^[UW][A-Z0-9]+$/.test(trimmed)) return "user";
  if (/^[CGD][A-Z0-9]+$/.test(trimmed)) return "channel";
  return "unknown";
}
function isSlackUserId(id) {
  return slackDestinationKind(id) === "user";
}
function isSlackChannelId(id) {
  return slackDestinationKind(id) === "channel";
}
function normalizeSlackDestinations(destinations, legacySingle) {
  const candidates2 = [...destinations ?? [], ...legacySingle ? [legacySingle] : []];
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const raw of candidates2) {
    const id = raw?.trim();
    if (!id || slackDestinationKind(id) === "unknown") continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

// src/calendar-trigger.ts
var DEFAULT_LEAD = { START: 30, END: 15 };
var MAX_LEAD_MINUTES = 24 * 60;
function parseCalendarFilter(raw, edge) {
  const filter = { leadMinutes: DEFAULT_LEAD[edge], calendarIds: [] };
  if (!raw?.trim()) return filter;
  for (const token of raw.split(",")) {
    const at = token.indexOf("=");
    if (at === -1) {
      const bare = token.trim();
      if (bare) filter.titleContains = bare;
      continue;
    }
    const key = token.slice(0, at).trim().toLowerCase();
    const value = token.slice(at + 1).trim();
    if (!value) continue;
    switch (key) {
      case "lead":
      case "minutes": {
        const minutes = Number(value);
        if (Number.isFinite(minutes) && minutes >= 0) {
          filter.leadMinutes = Math.min(Math.round(minutes), MAX_LEAD_MINUTES);
        }
        break;
      }
      case "title":
        filter.titleContains = value;
        break;
      case "calendar":
      case "calendars":
        filter.calendarIds.push(...value.split(/\s+/).filter(Boolean));
        break;
      default:
        break;
    }
  }
  return filter;
}
function calendarFireAt(event, edge, leadMinutes) {
  if (event.isAllDay) return null;
  const anchor = edge === "START" ? event.startsAt : event.endsAt;
  if (!anchor) return null;
  const offset = leadMinutes * 6e4;
  return new Date(edge === "START" ? anchor.getTime() - offset : anchor.getTime() + offset);
}
function calendarMeetingKey(event) {
  return event.eventId;
}
function serializeCalendarFilter(filter) {
  const parts = [`lead=${filter.leadMinutes}`];
  if (filter.titleContains?.trim()) parts.push(`title=${filter.titleContains.trim()}`);
  if (filter.calendarIds.length > 0) parts.push(`calendar=${filter.calendarIds.join(" ")}`);
  return parts.join(",");
}

// src/granola-trigger.ts
var KEYS = ["folder", "title", "title_is", "person"];
var TOKEN_SPLIT = new RegExp(`,(?=\\s*(?:${KEYS.join("|")})\\s*=)`, "i");
function parseGranolaFilter(raw) {
  const filter = { titleContains: [], titleEquals: [], people: [] };
  const value = raw?.trim();
  if (!value) return filter;
  if (!value.includes("=")) {
    filter.folderId = value;
    return filter;
  }
  for (const token of value.split(TOKEN_SPLIT)) {
    const at = token.indexOf("=");
    if (at === -1) continue;
    const key = token.slice(0, at).trim().toLowerCase();
    const text = token.slice(at + 1).trim();
    if (!text) continue;
    switch (key) {
      case "folder":
        filter.folderId = text;
        break;
      case "title":
        filter.titleContains.push(text);
        break;
      case "title_is":
        filter.titleEquals.push(text);
        break;
      case "person":
        filter.people.push(text.toLowerCase());
        break;
      default:
        break;
    }
  }
  return filter;
}
function serializeGranolaFilter(filter) {
  const parts = [];
  if (filter.folderId?.trim()) parts.push(`folder=${filter.folderId.trim()}`);
  for (const text of filter.titleContains) if (text.trim()) parts.push(`title=${text.trim()}`);
  for (const text of filter.titleEquals) if (text.trim()) parts.push(`title_is=${text.trim()}`);
  for (const text of filter.people ?? []) if (text.trim()) parts.push(`person=${text.trim().toLowerCase()}`);
  return parts.join(",");
}
function normalizeTitle(text) {
  return text.trim().replace(/\s+/g, " ").toLowerCase();
}
function granolaFilterHasTitleRule(filter) {
  return filter.titleContains.length > 0 || filter.titleEquals.length > 0;
}
function personMatches(pattern, address) {
  const p = pattern.trim().toLowerCase();
  const a = address.trim().toLowerCase();
  if (!p || !a) return false;
  if (!p.includes("*")) return p === a;
  const re = new RegExp(`^${p.split("*").map((part) => part.replace(/[.+?^${}()|[\]\\]/g, "\\$&")).join(".*")}$`);
  return re.test(a);
}
function matchesGranolaFilter(filter, note) {
  if (filter.folderId) {
    const folder = filter.folderId;
    if (note.source !== `granola:${folder}` && note.source !== folder) return false;
  }
  if (filter.people?.length) {
    const people = (note.people ?? []).filter((p) => typeof p === "string" && p.trim().length > 0);
    if (!people.some((address) => filter.people.some((pattern) => personMatches(pattern, address)))) return false;
  }
  if (!granolaFilterHasTitleRule(filter)) return true;
  const titles = note.titles.filter((t) => typeof t === "string" && t.trim().length > 0).map(normalizeTitle);
  if (titles.length === 0) return false;
  const contains = filter.titleContains.map(normalizeTitle);
  const equals = filter.titleEquals.map(normalizeTitle);
  return titles.some(
    (title) => contains.some((needle) => title.includes(needle)) || equals.some((exact) => title === exact)
  );
}
function describeGranolaFilter(filter) {
  const parts = [];
  if (filter.folderId) parts.push(`folder ${filter.folderId}`);
  const rules = [
    ...filter.titleContains.map((t) => `title contains \u201C${t}\u201D`),
    ...filter.titleEquals.map((t) => `title is \u201C${t}\u201D`)
  ];
  if (rules.length > 0) parts.push(rules.join(" or "));
  if (filter.people?.length) parts.push(`with ${filter.people.map((p) => `\u201C${p}\u201D`).join(" or ")}`);
  return parts.length > 0 ? parts.join(" \xB7 ") : null;
}

// src/mcp-client.ts
function mcpClientOf(userAgent) {
  if (!userAgent?.trim()) return null;
  const ua = userAgent.toLowerCase();
  if (ua.startsWith("claude") || ua.includes("anthropic")) return "claude";
  if (ua.startsWith("openai-mcp") || ua.includes("chatgpt") || ua.includes("openai")) return "chatgpt";
  if (ua.includes("cursor")) return "cursor";
  return "other";
}
var MCP_CLIENT_LABELS = {
  claude: "Claude",
  chatgpt: "ChatGPT",
  cursor: "Cursor",
  other: "an MCP client"
};

// src/feed-ingestion.ts
var PHASE_DETAIL = {
  starting: "The agent is starting on it",
  thinking: "The agent is thinking it through",
  reading: "Reading the pages it relates to",
  triaging: "Deciding which projects it belongs in",
  deciding: "Drafting the page changes",
  committing: "Saving the changes to the memory"
};
var HELD_DETAIL = {
  "duplicate-meeting": "The same meeting was already filed from another attendee\u2019s account",
  "reply-pending": "A reply to an automation\u2019s post, waiting for the chat to judge whether it is an answer",
  conversation: "A reply to an automation\u2019s post the chat judged to be conversation \u2014 kept for the automation, not sent to the memory",
  "superseded-version": "A newer version of this document arrived before this one was read; the newer one is absorbed instead, with these changes included",
  "unchanged-version": "This version says the same as the one already absorbed \u2014 only formatting or pictures changed \u2014 so there was nothing new to read",
  rule: "An ingest rule keeps this out of the memory",
  "rules-unanswered": "An ingest rule needs an answer the screener could not give yet \u2014 it is asked again every 15 minutes, and nothing is read until then"
};
function windowLabel(at, timezone) {
  if (!at) return "the next batch";
  const time = new Intl.DateTimeFormat("en-US", { timeZone: timezone, hour: "numeric", minute: "2-digit", timeZoneName: "short" }).format(at);
  return `the next batch at ${time}`;
}
function pillTime(at, timezone, now) {
  const clock2 = new Intl.DateTimeFormat("en-US", { timeZone: timezone, hour: "numeric", minute: "2-digit" }).format(at);
  const minutes = Math.max(0, Math.round((at.getTime() - now.getTime()) / 6e4));
  const until = minutes < 60 ? `in ${Math.max(1, minutes)} min` : `in ${Math.round(minutes / 60)} h`;
  return `${clock2} (${until})`;
}
function waitReason(segment) {
  return segment.runActivity?.startsWith("Waiting") ? segment.runActivity : null;
}
function feedIngestionState(facts, now = /* @__PURE__ */ new Date()) {
  const base = { runId: null, pages: facts.pagesWritten, nextWindowAt: null };
  const segments = facts.segments;
  const latestRun = segments.find((s) => s.runId)?.runId ?? null;
  if (segments.length === 0) {
    if (facts.sourceType === "USER_EDIT") {
      return { ...base, state: "held", label: "Record only", detail: "A hand edit, kept as the evidence for what was changed \u2014 already in the memory" };
    }
    return {
      ...base,
      state: "held",
      label: facts.heldReason === "duplicate-meeting" ? "Duplicate" : facts.heldReason === "conversation" ? "Record only" : facts.heldReason === "superseded-version" ? "Superseded" : facts.heldReason === "unchanged-version" ? "No changes" : facts.heldReason === "reply-pending" ? "Deciding" : facts.heldReason === "rule" ? "Held by rule" : facts.heldReason === "rules-unanswered" ? "Checking rules" : "Not ingested",
      detail: (facts.heldReason === "rule" || facts.heldReason === "rules-unanswered") && facts.heldDetail ? facts.heldDetail : (facts.heldReason && HELD_DETAIL[facts.heldReason]) ?? "Kept as a record and not sent for ingestion",
      ...facts.heldReason === "rule" || facts.heldReason === "rules-unanswered" ? { releasable: true } : {}
    };
  }
  const processing = segments.find((s) => s.status === "BATCHED" && s.runStatus === "PROCESSING");
  const waitingInPlace = processing && waitReason(processing);
  if (processing && waitingInPlace) {
    return { ...base, runId: processing.runId, state: "queued", label: "Queued", detail: waitingInPlace };
  }
  if (processing) {
    return {
      ...base,
      runId: processing.runId,
      state: "processing",
      label: "Processing",
      detail: (processing.runPhase && PHASE_DETAIL[processing.runPhase]) ?? "The agent is working on it now"
    };
  }
  const queued = segments.find((s) => s.status === "BATCHED" && (s.runStatus === "QUEUED" || s.runStatus === null));
  if (queued) {
    return {
      ...base,
      runId: queued.runId,
      state: "queued",
      label: "Queued",
      detail: waitReason(queued) ?? "Picked up for ingestion \u2014 waiting for an earlier run in the same project to finish"
    };
  }
  const waiting = segments.find((s) => s.status === "WAITING");
  if (waiting) {
    const next = waiting.window ? nextIngestWindow(waiting.window, now) : null;
    return {
      ...base,
      runId: latestRun,
      state: "waiting",
      label: waiting.attempts > 0 ? "Retrying" : next && waiting.window ? `Waiting \xB7 ${pillTime(next, waiting.window.timezone, now)}` : "Waiting",
      detail: waiting.attempts > 0 ? `An earlier attempt failed; it goes again with ${windowLabel(next, waiting.window?.timezone ?? "UTC")}` : waiting.window ? `Received \u2014 it will be read with ${windowLabel(next, waiting.window.timezone)}` : "Received \u2014 about to be picked up",
      nextWindowAt: next?.toISOString() ?? null
    };
  }
  const failed = segments.find((s) => s.status === "FAILED" || s.status === "BATCHED" && s.runStatus === "FAILED");
  if (failed) {
    return { ...base, runId: failed.runId, state: "failed", label: "Failed", detail: "Ingestion failed past its retries \u2014 see the run for why" };
  }
  const pages = (n) => `${n} page${n === 1 ? "" : "s"}`;
  if (facts.pagesWritten > 0) {
    return { ...base, runId: latestRun, state: "in_memory", label: "In memory", detail: `Written into ${pages(facts.pagesWritten)}` };
  }
  if (facts.pagesChangedByRun > 0 && facts.runItemCount <= 1) {
    return {
      ...base,
      pages: facts.pagesChangedByRun,
      runId: latestRun,
      state: "in_memory",
      label: "In memory",
      detail: `Changed ${pages(facts.pagesChangedByRun)}`
    };
  }
  if (facts.pagesChangedByRun > 0) {
    return {
      ...base,
      runId: latestRun,
      state: "read",
      label: "Read",
      detail: `Read in a batch of ${facts.runItemCount} that changed ${pages(facts.pagesChangedByRun)} \u2014 none quotes this item directly`
    };
  }
  return {
    ...base,
    runId: latestRun,
    state: "nothing_new",
    label: "Nothing new",
    detail: "Read by the agent \u2014 nothing in it was new to the memory, so nothing was changed"
  };
}

// src/guardrail.ts
var GUARDRAIL_CATEGORIES = [
  { key: "health", label: "Health & wellbeing", description: "Physical or mental health, diagnoses, symptoms, treatment, medication, disability, pregnancy, therapy, counselling, or wellbeing assessments." },
  { key: "family", label: "Family & personal life", description: "Partners, children, family circumstances, relationships, bereavement, caregiving, household composition, or other private-life details." },
  { key: "personal-finances", label: "Personal finances & compensation", description: "Individual salary, bonuses, equity, raises, debt, bankruptcy, financial hardship, bank details, or other personal financial information." },
  { key: "protected-characteristics", label: "Protected characteristics", description: "Race, ethnicity, religion, sexual orientation, gender identity, age, immigration status, union membership, or similar sensitive attributes." },
  {
    key: "personal-identifiers",
    label: "Personal identifiers",
    description: "Government IDs, passport/tax numbers, personal addresses, personal phone numbers, bank/card numbers, or other identifying information.",
    note: "Government IDs, passport or tax numbers, full personal bank or card numbers, home addresses. Order, invoice and account numbers, and the last four digits of a card on a receipt, do not count."
  },
  { key: "legal-matters", label: "Individual legal matters", description: "Criminal matters, personal litigation, immigration proceedings, disciplinary actions, grievances, or other individual legal exposure." },
  {
    key: "opinions-speculation",
    label: "Personal opinions, motives & speculation",
    description: "Guesses or claims about someone's mental state, personality, motives, intentions, emotions, or reasons for behaviour.",
    note: "Guesses or claims about a specific person\u2019s mental state, personality, motives or emotions. Opinions about products, games, features, designs, markets or strategy do not count."
  },
  { key: "performance-assessments", label: "Individual performance & behavioural assessments", description: "Personal performance evaluations, stress levels, personality assessments, behavioural judgements, or informal assessments of colleagues." },
  { key: "employment-contracts", label: "Employment offers & contracts", description: "Individual employment offers, employment contracts, termination terms, severance, or other person-specific employment arrangements." },
  { key: "employment-history", label: "Personal employment history", description: "Sensitive information about someone's current or former employment circumstances that is not needed for a business purpose." },
  { key: "conflicts-grievances", label: "Personal conflicts & grievances", description: "Interpersonal disputes, complaints, relationship conflicts, workplace conflicts, or personal grievances." },
  {
    key: "contact-location",
    label: "Personal contact & location",
    description: "Home addresses, private contact details, private travel/location information, or other personal whereabouts.",
    note: "Home addresses, private phone numbers and private whereabouts. Office addresses and work contact details do not count."
  },
  { key: "private-communications", label: "Private communications", description: "Personal emails, chats, messages, screenshots, correspondence, or conversations without a legitimate business purpose." },
  {
    key: "third-party",
    label: "Sensitive third-party information",
    description: "Sensitive personal information about customers, candidates, employees, contractors, vendors, or other individuals.",
    note: "Sensitive personal details about an individual outside the company (their health, finances, family, legal or employment situation). Naming a partner company, client contact, vendor or candidate in a business context does not count."
  },
  { key: "rumours", label: "Rumours, gossip & unverified claims", description: "Hearsay, rumours, gossip, allegations, or unsubstantiated claims about people or organisations." },
  { key: "preferences-lifestyle", label: "Private preferences & lifestyle", description: "Hobbies, dating or relationship preferences, lifestyle choices, political/religious views, or other personal preferences unrelated to business." },
  {
    key: "absence-circumstances",
    label: "Personal absence & circumstances",
    description: "Reasons for leave, illness, family emergencies, caregiving, or other private explanations for someone's availability.",
    note: "The private reason for someone being away (an illness, a family matter, a personal event). Saying only that someone is out or on leave does not count."
  },
  {
    key: "credentials",
    label: "Credentials & security secrets",
    description: "Passwords, API keys, access tokens, private keys, recovery codes, security answers, or other authentication secrets.",
    note: "Only when an actual secret value appears (a password, key, token or code), not a mention that one exists."
  },
  {
    key: "spam",
    label: "Spam & promotional content",
    description: "Spam, unsolicited messages, advertisements, marketing noise, or other non-business promotional material.",
    note: "Unsolicited advertising or marketing. Notifications from services the company uses are not spam.",
    wholeContent: true
  },
  {
    key: "non-business",
    label: "Non-business personal content",
    description: "Information primarily concerning someone's private life rather than a legitimate company activity, process, decision, or requirement.",
    note: "Only when the content as a whole is about someone\u2019s private life, not when one line of chit-chat appears in a business discussion.",
    wholeContent: true
  },
  {
    key: "restricted-business",
    label: "Restricted business information",
    description: "Confidential or restricted organisational information that should not enter a general knowledge base, such as privileged legal advice, unreleased strategy, or restricted security information.",
    note: "Only material explicitly marked confidential or privileged, legal advice, unannounced strategy said to be restricted, or security-sensitive detail such as vulnerabilities or access arrangements."
  },
  {
    key: "irrelevant",
    label: "Irrelevant / no business purpose",
    description: "Content with no identifiable business need, operational purpose, decision relevance, customer value, or institutional knowledge value.",
    note: "Only when the content as a whole has no business purpose at all.",
    wholeContent: true
  }
];
var PASSAGE_GUARDRAIL_CATEGORIES = GUARDRAIL_CATEGORIES.filter((category) => !category.wholeContent);
var CONTENT_GUARDRAIL_CATEGORIES = GUARDRAIL_CATEGORIES.filter((category) => category.wholeContent);
var GUARDRAIL_NOT_SENSITIVE = "Ordinary business content is not this: project and client work, decisions, plans, schedules, product discussion, contracts and agreements, invoices, receipts, pricing, partner and vendor correspondence, automated system notifications, and people named with their work email addresses or roles.";
var GUARDRAIL_CATEGORY_KEYS = GUARDRAIL_CATEGORIES.map((category) => category.key);
var PASSAGE_GUARDRAIL_KEYS = PASSAGE_GUARDRAIL_CATEGORIES.map((category) => category.key);
var CONTENT_GUARDRAIL_KEYS = CONTENT_GUARDRAIL_CATEGORIES.map((category) => category.key);
function isGuardrailCategory(key) {
  return GUARDRAIL_CATEGORIES.some((category) => category.key === key);
}
function guardrailCategoryLabel(key) {
  return GUARDRAIL_CATEGORIES.find((category) => category.key === key)?.label ?? key;
}
function renderGuardrailCategories(categories = GUARDRAIL_CATEGORIES) {
  return categories.map((category) => `- ${category.key}: ${category.note ?? category.description}`).join("\n");
}
export {
  ALL_PLAYGROUND_AGENTS,
  ALL_ROUTING_FEEDBACK_KINDS,
  ALL_ROUTING_FEEDBACK_STATUSES,
  ALL_UI_CAPABILITIES,
  ALL_UI_FEATURES,
  ALL_UI_FEATURE_FLAG_KEYS,
  ARRIVAL_OUTCOMES,
  AUTOMATION_TEMPLATES,
  AUTOMATION_TOOL_CATALOG,
  AUTOMATION_TRIGGERS,
  CHANGE_CLASSES,
  CONTENT_GUARDRAIL_CATEGORIES,
  CONTENT_GUARDRAIL_KEYS,
  DATE_PATTERN,
  DEFAULT_INGEST_RULE_THRESHOLD,
  DRIVE_SHARE_MODES,
  EMPTY_INGEST_POLICY,
  FEEDBACK_CHANNELS,
  GUARDRAIL_CATEGORIES,
  GUARDRAIL_CATEGORY_KEYS,
  GUARDRAIL_NOT_SENSITIVE,
  HUB_PROVIDERS,
  INGEST_INTERVALS,
  INGEST_INTERVAL_MINUTES,
  INGEST_MAIL_CLASSES,
  INGEST_MAIL_CLASS_LABELS,
  INGEST_RULE_LIMITS,
  INGEST_RULE_SOURCE_LABELS,
  INGEST_RULE_SOURCE_TYPES,
  INTEGRATION_PROVIDERS,
  INTEGRATION_SCOPES,
  KB_SWEEP_FINDING_KINDS,
  KB_SWEEP_FINDING_KIND_DESCRIPTIONS,
  KB_SWEEP_FINDING_KIND_LABELS,
  KB_SWEEP_FINDING_STATUSES,
  KB_SWEEP_FINDING_STATUS_LABELS,
  KB_SWEEP_SEVERITIES,
  KB_SWEEP_SEVERITY_LABELS,
  KB_SWEEP_TOOL_NAMES,
  LIVE_PLAYGROUND_STATUSES,
  MCP_CLIENT_LABELS,
  NAV_UI_FEATURES,
  NOOP_RUN_EVENT_SINK,
  OPEN_ROUTING_FEEDBACK_STATUSES,
  PASSAGE_GUARDRAIL_CATEGORIES,
  PASSAGE_GUARDRAIL_KEYS,
  PLAYGROUND_AGENTS,
  ROUTING_FEEDBACK_KINDS,
  ROUTING_FEEDBACK_STATUSES,
  RUN_PHASE_LABEL,
  SCHEDULE_TRIGGERS,
  SLACK_DELIVERY_FORMATS,
  SPACE_ACTIVITY_ACTIONS,
  TIME_OF_DAY_PATTERN,
  UI_CAPABILITIES,
  UI_FEATURES,
  UI_FEATURE_GROUPS,
  WEEKDAYS,
  addRunUsage,
  addressMatches,
  allCapabilitiesEnabled,
  allFeaturesEnabled,
  applicableIngestRules,
  automationTemplate,
  automationToolDefinition,
  automationToolDescription,
  automationTriggerDefinition,
  automationTriggerLabel,
  buildMetricsDashboard,
  calendarFireAt,
  calendarMeetingKey,
  capabilitiesOf,
  classifyArrival,
  classifyChange,
  decideIngestRules,
  describeGranolaFilter,
  describeIngestConditions,
  describeIngestRules,
  describeIngestWindow,
  describeSchedule,
  emptyPlaygroundExpectation,
  emptyPlaygroundSpec,
  emptyRunTimeline,
  emptyRunUsage,
  expectationAsserts,
  feedIngestionState,
  firstEnabledFeature,
  foldRunEvent,
  foldRunEvents,
  formatRunCost,
  formatRunTokens,
  granolaFilterHasTitleRule,
  granolaMeetingKey,
  guardrailCategoryLabel,
  hasSubstantiveChange,
  ingestConditionsMatch,
  ingestItemFacts,
  ingestJevQuestion,
  ingestWindowSlots,
  isBaselineSpec,
  isDurableRunEvent,
  isEmptyIngestPolicy,
  isGuardrailCategory,
  isLiveSweepFindingStatus,
  isPlaygroundRunLive,
  isRetrieveMetrics,
  isRuledSourceType,
  isScheduleTrigger,
  isSlackChannelId,
  isSlackUserId,
  isSpaceTextType,
  isTerminalPhase,
  isValidDateIso,
  isValidDayOfMonth,
  isValidTimeOfDay,
  localDateIn,
  markdownSyntaxIndices,
  matchesGranolaFilter,
  mcpClientOf,
  measureEdit,
  modelRetryConfig,
  mondayOf,
  nextIngestWindow,
  nextOccurrence,
  normalizeDaysOfWeek,
  normalizeIngestPolicy,
  normalizeQuote,
  normalizeRenderedQuote,
  normalizeSlackDestinations,
  paragraphKey,
  parseCalendarFilter,
  parseGranolaFilter,
  parsePromptCacheTtl,
  phaseForTool,
  playgroundAgent,
  previousIngestWindow,
  previousOccurrence,
  promptCacheParams,
  quoteAppearsIn,
  readIngestPolicy,
  renderGuardrailCategories,
  resolveCapabilityFlags,
  resolveFeatureFlags,
  routingFeedbackKind,
  routingFeedbackStatus,
  runUsageTokens,
  safeTimeZone,
  senderDomainOf,
  serializeCalendarFilter,
  serializeGranolaFilter,
  slackDestinationKind,
  slackFormatProblem,
  spaceBaseType,
  spaceContentOf,
  splitParagraphs,
  stripInlineMarkdown,
  summarizeFeedback,
  sweepFindingShortId,
  sweepSeverityFor,
  sweepSeverityRank,
  toolSettingOf,
  validateIngestWindow,
  validateSchedule,
  zonedInstant
};
//# sourceMappingURL=index.mjs.map